import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable } from "rxjs";

import { UserTableService } from 'src/service/user.service';

import { CustomerService, DataService, FireForesightService, SiteTypeService, BurnListService, GeoAssetService, SiteTwinService } from 'src/api/services';
import { User, Customer, CustomerSummary, StatusType, Status, Incident, Widget, Note, SiteTwin, Statistics, BurnList, GeoAssetDetailDTO, GeoAssetDetailWithGeoJson, BurnListStatus, BurnListComment, BurnListRegion, SiteType } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { BurnListDataService } from 'src/service/burn-list-data.service';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { AzureAuthService } from 'src/service/azure-auth.service';
import { FireService } from 'src/service/fire.service';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { MapComponent } from 'src/app/components/map/map.component';
import { DashboardComponent } from 'src/app/views/dashboard/dashboard.component';

import * as L from 'leaflet';
import * as WindBarb from 'src/app/components/map/leaflet.windbarb';
import { isNumber } from 'highcharts';
import { DashboardSelection } from 'src/app/models/dashboard';

@Component({
  selector: 'app-fire-foresight-burn-list',
  templateUrl: './fire-foresight-burn-list.component.html',
  styleUrls: ['./fire-foresight-burn-list.component.css']
})
export class FireForesightBurnListComponent implements OnInit, OnDestroy {
  @ViewChild('createBurnModal', { static: false }) createBurnModal: ModalDirective;
  @ViewChild('addCoupModal', { static: false }) addCoupModal: ModalDirective;
  @ViewChild('moveCoupModal', { static: false }) moveCoupModal: ModalDirective;
  @ViewChild('coupMap', { read: MapComponent, static: false }) map: MapComponent;
  @ViewChild('burnListMap', { read: MapComponent, static: false }) burnListMap: MapComponent;
  @ViewChild('dashboardViewer', { read: DashboardComponent, static: true }) dashboard: DashboardComponent;
  
  public loading: boolean = false;
  public plannedBurns: BurnList[] = [];
  public selectedBurn: BurnList = null;
  public selectedBurnId: number = null;
  
  // Geo assets (burn coups)
  public allCoups: GeoAssetDetailWithGeoJson[] = [];
  public coupsLoading: boolean = false;
  public coupLayers: any = {};
  public coupTypeId: number | null = null;
  private mapRetryCount: number = 0;
  private maxMapRetries: number = 20;
  
  // Burn list map (overview of all burns)
  public burnListMapLayers: any = {};
  private burnListMapRetryCount: number = 0;
  
  // Configurable zoom level for loading geoassets
  public minZoomLevelForGeoAssets: number = 12; // Only load geoassets when zoom >= 12
  public currentZoomLevel: number = 0; // Track current map zoom level
  private readonly ZOOM_BUFFER_FOR_BUTTON: number = 2; // Allow button to work 2 zoom levels further out
  private mapInitialized: boolean = false;
  private shouldFitBounds: boolean = false; // Control when to auto-fit map bounds
  private loadDebounceTimer: any = null; // Timer for debouncing map movement
  public loadDebounceDelay: number = 500; // Delay in ms before loading after map movement stops
  private isInitialLoad: boolean = false; // Flag to prevent event listeners from firing during initial load
  private lastLoadedBounds: { minLat: number, minLon: number, maxLat: number, maxLon: number } | null = null; // Track last loaded bounds
  
  public siteTypes: SiteType[] = null;

  // New burn form properties
  public newBurn: Partial<BurnList> = {
    planName: '',
    expectedStartTime: null,
    expectedEndTime: null,
    burnListRegionId: null,
    burnListTypeId: null
  };
  public savingBurn: boolean = false;
  
  // Burn list lookups
  public burnListStatuses: BurnListStatus[] = [];
  public burnListRegions: BurnListRegion[] = [];
  public burnListTypes: BurnListStatus[] = []; // Using BurnListStatus as the type is reused
  public statusesLoading: boolean = false;
  
  // Site twins for burn coups
  public burnCoupSiteTwins: Map<number, SiteTwin> = new Map();
  public siteTwinsLoading: boolean = false;

  // Move burn coup properties
  public coupToMove: any = null;
  public targetBurnListId: number | null = null;
  public createNewTargetList: boolean = false;
  public newTargetBurnList: Partial<BurnList> = {
    planName: '',
    expectedStartTime: null,
    burnListRegionId: null,
    burnListTypeId: null,
    customerId: 0
  };
  public movingCoup: boolean = false;
  public availableTargetBurnLists: BurnList[] = [];

  // EPA Air Quality site twins
  public airQualitySites: any[] = [];
  public airQualityMarkers: any[] = [];
  public airQualitySitesLoading: boolean = false;
  private airQualityRefreshInterval: any = null;
  private readonly AIR_QUALITY_REFRESH_MS = 5 * 60 * 1000; // 5 minutes
  public airQualitySiteType: SiteType = null;

  // Fuel Moisture sensor site twins (for map display)
  public fuelMoistureSensorSites: any[] = [];
  public fuelMoistureSensorMarkers: any[] = [];
  public fuelMoistureSensorSitesLoading: boolean = false;
  public fuelMoistureSiteType: SiteType = null;

  // Weather Station sites
  public weatherStationSites: any[] = [];
  public weatherStationMarkers: any[] = [];
  public weatherStationSitesLoading: boolean = false;
  public weatherStationSiteType: SiteType = null;

  // Fuel moisture refresh and change tracking
  private fuelMoistureRefreshInterval: any = null;
  private readonly FUEL_MOISTURE_REFRESH_MS = 5 * 60 * 1000; // 2 minutes
  private previousFuelMoistureValues: Map<string, { shaded: string | null, unshaded: string | null }> = new Map();
  public updatedFuelMoistureValues: Map<string, { shaded: boolean, unshaded: boolean }> = new Map();

  // Edit mode for selected burn
  public isEditMode: boolean = false;
  public editingBurn: Partial<BurnList> = {};

  // Tab management
  public activeTab: 'details' | 'journal' = 'details';
  public newComment: string = '';
  public addingComment: boolean = false;

  // Coup search modal
  public coupSearchQuery: string = '';
  public coupSearchResults: GeoAssetDetailDTO[] = [];
  public coupSearching: boolean = false;
  private coupSearchDebounceTimer: any = null;

  // Accordion state management for region and type grouping
  public openRegionIds: Set<number> = new Set(); // Track which regions are expanded
  public openTypeIds: Map<number, Set<number>> = new Map(); // Track which types are expanded within each region
  public openDashboardRegions: Set<number> = new Set(); // Track which dashboard regions are expanded in the menu

  constructor(
    public app: ApplicationConfigService,
    public security: AzureAuthService,
    private svc: FireForesightService,
    public fire: FireService,
    private siteTypeService: SiteTypeService,
    private dataService: DataService,
    private toasterService: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private burnListService: BurnListService,
    private geoAssetService: GeoAssetService,
    private siteTwinService: SiteTwinService,
    private burnListDataService: BurnListDataService
  ) {
    app.customerChanged$.subscribe(cid => {
      this.ngOnInit();
    });
  }

  ngOnInit(): void {
    console.log("FireForesightBurnListComponent initialized");
    if (this.app.customerId == null) {
      this.app.customerId = this.security.userProfile.customerId;
    }

    // Load persisted accordion state
    this.loadAccordionState();

    // Check if there's a burnListId in the route params
    this.route.params.subscribe(params => {
      const burnListId = params['burnListId'];
      if (burnListId) {
        this.selectedBurnId = parseInt(burnListId);
        // Load the specific burn list after planned burns are loaded
        this.loadSpecificBurnList(this.selectedBurnId);
      }
    });

    this.loadBurnListStatuses();
    this.loadBurnListRegions();
    this.loadBurnListTypes();
    this.loadPlannedBurns();
    this.loadBurnCoupType(); // Just load the type ID, don't load coups yet
    this.startAirQualityRefreshTimer();
    this.startFuelMoistureRefreshTimer();   
  }

  ngOnDestroy(): void {
    // Clear debounce timer on component destroy
    if (this.loadDebounceTimer) {
      clearTimeout(this.loadDebounceTimer);
      this.loadDebounceTimer = null;
    }
    
    // Clear fuel moisture refresh timer
    if (this.fuelMoistureRefreshInterval) {
      clearInterval(this.fuelMoistureRefreshInterval);
      this.fuelMoistureRefreshInterval = null;
    }
    
    // Clear air quality refresh timer
    if (this.airQualityRefreshInterval) {
      clearInterval(this.airQualityRefreshInterval);
      this.airQualityRefreshInterval = null;
    }
    
    // Remove map event listeners if map exists
    if (this.map && this.map.map) {
      this.map.map.off('moveend');
      this.map.map.off('zoomend');
    }

    // Remove air quality markers
    this.removeAirQualityMarkers();
    
    // Remove fuel moisture sensor markers
    this.removeFuelMoistureSensorMarkers();
    
    // Remove weather station markers
    this.removeWeatherStationMarkers();
  }

  selectedSiteUri: string | null = null;
  selectedDashboardId: number | null = null;
  openDashboard(site, siteType){
    this.selectedSiteUri = site.geoAsset?.identifier?.toLowerCase() || null;
    if (this.selectedSiteUri == null){
      this.selectedSiteUri = site.siteUri;
    }
    this.selectedDashboardId = siteType.dashboards[0].dashboardId;
    console.log("Openning dashboard for site:", site);
    this.dashboard?.reload({dashboardId:this.selectedDashboardId, siteUri:this.selectedSiteUri} as DashboardSelection);
  }

  loadBurnListStatuses(): void {
    this.statusesLoading = true;
    this.burnListDataService.getBurnListStatuses().subscribe(
      (statuses) => {
        this.burnListStatuses = statuses;
        this.statusesLoading = false;
      },
      (error) => {
        console.error('Failed to load burn list statuses', error);
        this.toasterService.error('Failed to load burn list statuses');
        this.statusesLoading = false;
      }
    );
  }

  loadBurnListRegions(): void {
    this.burnListDataService.getBurnListRegions().subscribe(
      (regions) => {
        this.burnListRegions = regions;
      },
      (error) => {
        console.error('Failed to load burn list regions', error);
        this.toasterService.error('Failed to load burn list regions');
      }
    );
  }

  loadBurnListTypes(): void {
    this.burnListDataService.getBurnListTypes().subscribe(
      (types) => {
        this.burnListTypes = types;
      },
      (error) => {
        console.error('Failed to load burn list types', error);
        this.toasterService.error('Failed to load burn list types');
      }
    );
  }

  loadBurnCoupType(): void {
    // Load geo asset types to find the "Burn Coup" type
    this.geoAssetService.GeoAssetGetAllGeoAssetTypes(this.app.customerId).subscribe(
      (types) => {
        const coupType = types.find(t => t.name && t.name.toLowerCase().includes('coup'));
        if (coupType) {
          this.coupTypeId = coupType.id;
        }
        console.log('Burn coup type ID:', this.coupTypeId);
        // Don't load coups here - wait until burn plan is selected and map is visible
      },
      (error) => {
        console.error('Failed to load geo asset types', error);
      }
    );
  }

  loadBurnCoupsForCurrentView(): void {
    // Only load if map is ready and zoom level is sufficient
    if (!this.map || !this.map.map) {
      console.log('Map not ready yet for loading coups');
      return;
    }

    const currentZoom = this.map.map.getZoom();
    if (currentZoom < this.minZoomLevelForGeoAssets) {
      console.log(`Zoom level ${currentZoom} is below minimum ${this.minZoomLevelForGeoAssets}, keeping existing coups but not loading new ones`);
      // Keep existing coups visible, just don't load new ones
      return;
    }

    const bounds = this.map.map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();

    const currentBounds = {
      minLat: sw.lat,
      minLon: sw.lng,
      maxLat: ne.lat,
      maxLon: ne.lng
    };

    // Check if current view is within previously loaded bounds
    if (this.lastLoadedBounds && this.isWithinLoadedBounds(currentBounds)) {
      console.log('Current view is within already loaded bounds - skipping reload');
      console.log('Current:', currentBounds);
      console.log('Last loaded:', this.lastLoadedBounds);
      return;
    }

    console.log('Loading coups for bounds:', currentBounds);

    this.coupsLoading = true;
    const params: any = {
      customerId: this.app.customerId,
      topLeftLat: currentBounds.minLat,
      topLeftLon: currentBounds.minLon,
      bottomRightLat: currentBounds.maxLat,
      bottomRightLon: currentBounds.maxLon
    };
    
    if (this.coupTypeId) {
      params.assetTypeId = this.coupTypeId;
    }

    this.geoAssetService.GeoAssetGetAllGeoAssetsWithinBoundingBox(params).subscribe(
      (coups) => {
        this.allCoups = coups;
        this.coupsLoading = false;
        this.lastLoadedBounds = currentBounds; // Store the bounds we just loaded
        this.displayCoupsOnMap();
      },
      (error) => {
        console.error('Failed to load burn coups', error);
        this.toasterService.error('Failed to load burn coups');
        this.coupsLoading = false;
      }
    );
  }

  private isWithinLoadedBounds(currentBounds: { minLat: number, minLon: number, maxLat: number, maxLon: number }): boolean {
    if (!this.lastLoadedBounds) {
      return false;
    }

    // Check if current bounds are completely within the last loaded bounds
    return currentBounds.minLat >= this.lastLoadedBounds.minLat &&
           currentBounds.maxLat <= this.lastLoadedBounds.maxLat &&
           currentBounds.minLon >= this.lastLoadedBounds.minLon &&
           currentBounds.maxLon <= this.lastLoadedBounds.maxLon;
  }

  // Remove map event listeners and debounce logic for auto-loading coups

  loadVisibleCoups(): void {
    this.loadBurnCoupsForCurrentView();
  }

  /**
   * Check if Load Visible Coups button should be enabled
   * Allows loading at up to 2 zoom levels further out than minimum
   */
  isLoadCoupsButtonEnabled(): boolean {
    if (!this.map || !this.map.map) {
      return false;
    }
    const minZoomForButton = this.minZoomLevelForGeoAssets - this.ZOOM_BUFFER_FOR_BUTTON;
    return this.currentZoomLevel >= minZoomForButton;
  }

  /**
   * Update current zoom level tracking
   */
  updateZoomLevel(): void {
    if (this.map && this.map.map) {
      this.currentZoomLevel = this.map.map.getZoom();
    }
  }

  /**
   * Update coup label visibility based on current zoom level
   */
  updateCoupLabelVisibility(): void {
    if (!this.map || !this.map.map) {
      return;
    }

    const shouldShowLabels = this.currentZoomLevel > 12;

    Object.keys(this.coupLayers).forEach(key => {
      const coupLayer = this.coupLayers[key];
      if (coupLayer && coupLayer.label) {
        // Remove from map if currently visible but shouldn't be
        if (this.map.map.hasLayer(coupLayer.label) && !shouldShowLabels) {
          this.map.map.removeLayer(coupLayer.label);
        }
        // Add to map if currently hidden but should be visible
        else if (!this.map.map.hasLayer(coupLayer.label) && shouldShowLabels) {
          coupLayer.label.addTo(this.map.map);
        }
      }
    });
  }

  clearCoupsFromMap(): void {
    if (!this.map || !this.map.map) {
      return;
    }

    // Clear existing coup layers
    Object.keys(this.coupLayers).forEach(key => {
      if (this.coupLayers[key]) {
        if (this.coupLayers[key].polygon) {
          this.map.map.removeLayer(this.coupLayers[key].polygon);
        }
        if (this.coupLayers[key].label) {
          this.map.map.removeLayer(this.coupLayers[key].label);
        }
      }
    });
    this.coupLayers = {};
    this.allCoups = [];
    this.lastLoadedBounds = null; // Reset bounds when clearing
    console.log('Cleared all coups from map');
  }

  displayBurnListsOnMap(): void {
    console.log('displayBurnListsOnMap called, attempt:', this.burnListMapRetryCount + 1);
    console.log('BurnListMap exists?', !!this.burnListMap);
    console.log('BurnListMap.map exists?', this.burnListMap?.map);
    console.log('Number of burn lists:', this.plannedBurns.length);
    
    if (!this.burnListMap || !this.burnListMap.map) {
      if (this.burnListMapRetryCount < this.maxMapRetries) {
        this.burnListMapRetryCount++;
        console.log(`Burn list map not ready, retrying in 300ms... (attempt ${this.burnListMapRetryCount}/${this.maxMapRetries})`);
        setTimeout(() => this.displayBurnListsOnMap(), 300);
      } else {
        console.error('Burn list map failed to initialize after', this.maxMapRetries, 'attempts');
      }
      return;
    }
    
    // Reset retry counter on success
    this.burnListMapRetryCount = 0;
    
    console.log('Burn list map is ready, displaying circles for burn lists...');

    // Clear existing layers
    Object.keys(this.burnListMapLayers).forEach(key => {
      if (this.burnListMapLayers[key]) {
        if (this.burnListMapLayers[key].circle) {
          this.burnListMap.map.removeLayer(this.burnListMapLayers[key].circle);
        }
        if (this.burnListMapLayers[key].label) {
          this.burnListMap.map.removeLayer(this.burnListMapLayers[key].label);
        }
      }
    });
    this.burnListMapLayers = {};

    const circleGroup: any[] = [];

    // Filter burn lists to only show Sub Region type and exclude draft/completed
    const filteredBurnLists = this.plannedBurns.filter(burnList => {
      // Get type name
      const type = this.burnListTypes.find(t => t.id === burnList.burnListTypeId);
      const typeName = type?.name?.toLowerCase() || '';
      
      // Get status name
      const statusName = this.getBurnListStatusName(burnList.burnListStatusId).toLowerCase();
      
      // Only include Sub Region type
      const isSubRegion = typeName.includes('sub') && typeName.includes('region');
      
      // Exclude draft and completed statuses
      const isNotDraftOrCompleted = !statusName.includes('draft') && 
                                    !statusName.includes('completed') && 
                                    !statusName.includes('finished');
      
      return isSubRegion && isNotDraftOrCompleted;
    });

    console.log(`Filtered ${filteredBurnLists.length} burn lists from ${this.plannedBurns.length} total (Sub Region only, excluding draft/completed)`);

    // Display a circle for each filtered burn list
    filteredBurnLists.forEach(burnList => {
      if (burnList.burnCoups && burnList.burnCoups.length > 0) {
        try {
          // Calculate the center and radius that encompasses all burn coups
          const boundsInfo = this.calculateBurnListBounds(burnList);
          
          if (!boundsInfo) {
            console.warn(`Could not calculate bounds for burn list ${burnList.id}`);
            return;
          }

          // Color by burn list status
          const statusColor = this.getBurnListStatusColor(burnList.burnListStatusId);
          const fillColor = this.getColorFromStatusClass(statusColor);
          
          // Create circle
          const circle = L.circle([boundsInfo.center.lat, boundsInfo.center.lng], {
            radius: boundsInfo.radius,
            fillColor: fillColor,
            color: fillColor,
            weight: 2,
            opacity: 0.8,
            fillOpacity: 0.3
          });

          // Add popup
          const popupContent = `
            <div style="min-width: 200px;">
              <h6 class="fw-bold mb-2">${burnList.planName || 'Burn List #' + burnList.id}</h6>
              <div class="mb-2">
                <strong>Coups:</strong> ${burnList.burnCoups.length}<br>
                <strong>Status:</strong> <span class="badge ${statusColor}">${this.getBurnListStatusName(burnList.burnListStatusId)}</span><br>
                ${burnList.expectedStartTime ? '<strong>Start:</strong> ' + new Date(burnList.expectedStartTime).toLocaleString() + '<br>' : ''}
              </div>
              <button id="view-burn-${burnList.id}" class="btn btn-sm btn-primary w-100">
                <i class="bi bi-eye me-1"></i> View Burn List
              </button>
            </div>
          `;
          circle.bindPopup(popupContent);

          // Add click handler for view button
          circle.on('popupopen', () => {
            const viewButton = document.getElementById(`view-burn-${burnList.id}`);
            if (viewButton) {
              viewButton.onclick = () => {
                this.selectBurn(burnList);
                this.burnListMap.map.closePopup();
              };
            }
          });

          circle.addTo(this.burnListMap.map);
          circleGroup.push(circle);
          
          // Add label at center with burn list name and coup count
          const textLabel = L.marker([boundsInfo.center.lat, boundsInfo.center.lng], {
            icon: L.divIcon({
              className: 'burn-list-label',
              html: `
                <div class="burn-list-label-content" style="
                  background: ${fillColor};
                  color: white;
                  padding: 8px 12px;
                  border-radius: 20px;
                  font-weight: bold;
                  font-size: 14px;
                  text-align: center;
                  box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                  border: 2px solid white;
                  white-space: nowrap;
                  width: 150px;
                  line-height: 1.3;
                ">
                  <div style="margin-bottom: 2px;">${burnList.planName || 'Burn #' + burnList.id}</div>
                  <small style="font-size: 11px; display: block;">${burnList.burnCoups.length} coups</small>
                </div>
              `,
              iconSize: [1, 1]
            }),
            interactive: false
          });
          
          textLabel.addTo(this.burnListMap.map);
          this.burnListMapLayers[burnList.id] = { circle: circle, label: textLabel };

        } catch (error) {
          console.error(`Failed to display circle for burn list ${burnList.id}:`, error);
        }
      }
    });

    // Fit bounds to show all circles
    if (circleGroup.length > 0) {
      const group = L.featureGroup(circleGroup);
      this.burnListMap.map.fitBounds(group.getBounds(), { padding: [50, 50] });
    }

    console.log(`Displayed ${circleGroup.length} burn list circles`);
  }

  /**
   * Calculate the center point and radius to encompass all burn coups in a burn list
   */
  calculateBurnListBounds(burnList: any): { center: { lat: number, lng: number }, radius: number } | null {
    const coordinates: { lat: number, lng: number }[] = [];

    burnList.burnCoups.forEach((burnCoup: any) => {
      if (burnCoup.geoAsset && burnCoup.geoAsset.geometryGeoJson) {
        try {
          const geoJson = typeof burnCoup.geoAsset.geometryGeoJson === 'string' 
            ? JSON.parse(burnCoup.geoAsset.geometryGeoJson) 
            : burnCoup.geoAsset.geometryGeoJson;
          
          // Extract coordinates from GeoJSON
          const coupCoords = this.extractCoordinatesFromGeoJson(geoJson);
          coordinates.push(...coupCoords);
        } catch (error) {
          console.error(`Failed to parse geometry for burn coup ${burnCoup.id}:`, error);
        }
      }
    });

    if (coordinates.length === 0) {
      return null;
    }

    // Calculate center (average of all coordinates)
    const center = {
      lat: coordinates.reduce((sum, coord) => sum + coord.lat, 0) / coordinates.length,
      lng: coordinates.reduce((sum, coord) => sum + coord.lng, 0) / coordinates.length
    };

    // Calculate radius (distance from center to furthest point)
    let maxDistance = 0;
    coordinates.forEach(coord => {
      const distance = this.calculateDistanceInMeters(center.lat, center.lng, coord.lat, coord.lng);
      if (distance > maxDistance) {
        maxDistance = distance;
      }
    });

    // Add 10% padding to the radius
    const radius = maxDistance * 1.1;

    return { center, radius };
  }

  /**
   * Extract all coordinates from GeoJSON geometry
   */
  extractCoordinatesFromGeoJson(geoJson: any): { lat: number, lng: number }[] {
    const coords: { lat: number, lng: number }[] = [];

    let geometry = geoJson;
    
    // Handle Feature wrapper
    if (geoJson.type === 'Feature' && geoJson.geometry) {
      geometry = geoJson.geometry;
    }
    
    // Handle FeatureCollection
    if (geoJson.type === 'FeatureCollection' && geoJson.features && geoJson.features.length > 0) {
      geoJson.features.forEach((feature: any) => {
        if (feature.geometry) {
          coords.push(...this.extractCoordinatesFromGeoJson(feature.geometry));
        }
      });
      return coords;
    }

    if (!geometry || !geometry.coordinates) {
      return coords;
    }

    // Handle different geometry types
    if (geometry.type === 'Point') {
      coords.push({ lng: geometry.coordinates[0], lat: geometry.coordinates[1] });
    } else if (geometry.type === 'LineString') {
      geometry.coordinates.forEach((coord: number[]) => {
        coords.push({ lng: coord[0], lat: coord[1] });
      });
    } else if (geometry.type === 'Polygon') {
      geometry.coordinates[0].forEach((coord: number[]) => {
        coords.push({ lng: coord[0], lat: coord[1] });
      });
    } else if (geometry.type === 'MultiPolygon') {
      geometry.coordinates.forEach((polygon: number[][][]) => {
        polygon[0].forEach((coord: number[]) => {
          coords.push({ lng: coord[0], lat: coord[1] });
        });
      });
    }

    return coords;
  }

  /**
   * Calculate distance between two points in meters using Haversine formula
   */
  calculateDistanceInMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371000; // Earth's radius in meters
    const dLat = this.toRadians(lat2 - lat1);
    const dLng = this.toRadians(lng2 - lng1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
              Math.sin(dLng / 2) * Math.sin(dLng / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return distance;
  }


  getBurnListStatusColor(statusId: number): string {
    const status = this.burnListStatuses.find(s => s.id === statusId);
    const statusName = status?.name?.toLowerCase() || '';
    
    if (statusName.includes('completed') || statusName.includes('finished')) {
      return 'bg-success';
    } else if (statusName.includes('progress') || statusName.includes('active') || statusName.includes('start')) {
      return 'bg-warning';
    } else if (statusName.includes('planned') || statusName.includes('scheduled')) {
      return 'bg-info';
    } else if (statusName.includes('cancelled') || statusName.includes('abandoned')) {
      return 'bg-danger';
    }
    return 'bg-secondary';
  }

  getColorFromStatusClass(statusClass: string): string {
    const colorMap: { [key: string]: string } = {
      'bg-success': '#28a745',
      'bg-warning': '#ffc107',
      'bg-info': '#17a2b8',
      'bg-danger': '#dc3545',
      'bg-secondary': '#6c757d'
    };
    return colorMap[statusClass] || '#6c757d';
  }

  getBurnListStatusName(statusId: number): string {
    const status = this.burnListStatuses.find(s => s.id === statusId);
    return status?.name || 'Unknown';
  }


  displayCoupsOnMap(): void {
    console.log('displayCoupsOnMap called, attempt:', this.mapRetryCount + 1);
    console.log('Map exists?', !!this.map);
    console.log('Map.map exists?', this.map?.map);
    console.log('Number of coups to display:', this.allCoups.length);
    
    if (!this.map || !this.map.map) {
      if (this.mapRetryCount < this.maxMapRetries) {
        this.mapRetryCount++;
        console.log(`Map not ready, retrying in 300ms... (attempt ${this.mapRetryCount}/${this.maxMapRetries})`);
        setTimeout(() => this.displayCoupsOnMap(), 300);
      } else {
        console.error('Map failed to initialize after', this.maxMapRetries, 'attempts');
        //this.toasterService.error('Failed to initialize map');
      }
      return;
    }
    
    // Reset retry counter on success
    this.mapRetryCount = 0;
    
    // Update zoom level tracking
    this.updateZoomLevel();
    
    // Set up zoom event listener to track zoom changes
    if (!this.mapInitialized) {
      this.map.map.on('zoomend', () => {
        this.updateZoomLevel();
        this.updateCoupLabelVisibility();
      });
      this.mapInitialized = true;
    }
    
    console.log('Map is ready, displaying', this.allCoups.length, 'coups...');

    // Clear existing coup layers
    Object.keys(this.coupLayers).forEach(key => {
      if (this.coupLayers[key]) {
        if (this.coupLayers[key].polygon) {
          this.map.map.removeLayer(this.coupLayers[key].polygon);
        }
        if (this.coupLayers[key].label) {
          this.map.map.removeLayer(this.coupLayers[key].label);
        }
      }
    });
    this.coupLayers = {};

    const selectedCoupIds = this.selectedBurn?.burnCoups?.map(bc => bc.geoAsset?.id) || [];
    console.log('Selected coup IDs:', selectedCoupIds);

    this.allCoups.forEach(coup => {
      if (coup.geometry) {
        try {
          // Parse geometry if it's a string, otherwise use as-is
          const geoJson = typeof coup.geometry === 'string' 
            ? JSON.parse(coup.geometry) 
            : coup.geometry;
          // Always use geoAsset.id for selection
          const coupId = coup.id;
          const isSelected = selectedCoupIds.includes(coupId);
          
          // Check if the coup is disabled
          const burnCoup = isSelected ? this.selectedBurn?.burnCoups?.find(bc => bc.geoAsset?.id === coupId) : null;
          const isDisabled = burnCoup?.disabled || false;

          // Validate that we have valid GeoJSON
          if (!geoJson || (!geoJson.type && !geoJson.geometry)) {
            console.error(`Invalid GeoJSON structure for coup ${coupId}:`, geoJson);
            return;
          }

          // Determine colors based on state and fuel moisture
          let fillColor: string;
          let borderColor: string;
          let fillOpacity: number;
          
          if (isDisabled) {
            fillColor = '#999999';
            borderColor = '#666666';
            fillOpacity = 0.4;
          } else if (isSelected) {
            // For selected coups, use fuel moisture color if available
            const fuelMoistureColor = burnCoup ? this.getCoupColorByFuelMoisture(burnCoup.id) : null;
            if (fuelMoistureColor) {
              console.log(`Applying fuel moisture color ${fuelMoistureColor} to coup ${coupId} (burnCoup ${burnCoup.id})`);
              fillColor = fuelMoistureColor;
              borderColor = fuelMoistureColor;
            } else {
              // Default selected color (orange) if no fuel moisture data
              console.log(`No fuel moisture data for coup ${coupId}, using default orange`);
              fillColor = '#ff7800';
              borderColor = '#ff7800';
            }
            fillOpacity = 0.7;
          } else {
            fillColor = '#3388ff';
            borderColor = '#3388ff';
            fillOpacity = 0.2;
          }

          const layer = L.geoJSON(geoJson, {
            style: {
              fillColor: fillColor,
              weight: 2,
              opacity: 1,
              color: borderColor,
              fillOpacity: fillOpacity
            },
            onEachFeature: (feature, layer) => {
              const popupContent = this.createCoupPopupContent(coup, isSelected, burnCoup);
              const popup = L.popup().setContent(popupContent);
              layer.bindPopup(popup);

              // Add click handler for the buttons after popup opens
              layer.on('popupopen', () => {
                // Add/Remove button handler
                const toggleButtonId = `coup-toggle-${coupId}`;
                const toggleButton = document.getElementById(toggleButtonId);
                if (toggleButton) {
                  toggleButton.onclick = () => this.toggleCoupInBurnPlan(coup.id);
                }
                
                // Enable/Disable button handler
                const enableDisableButtonId = `coup-enable-disable-${coupId}`;
                const enableDisableButton = document.getElementById(enableDisableButtonId);
                if (enableDisableButton) {
                  const burnCoupId = parseInt(enableDisableButton.getAttribute('data-burn-coup-id'));
                  const burnCoup = this.selectedBurn?.burnCoups?.find(bc => bc.geoAsset?.id === coupId);
                  const isDisabled = burnCoup?.disabled || false;
                  
                  enableDisableButton.onclick = () => {
                    if (isDisabled) {
                      this.enableBurnCoup(burnCoupId);
                    } else {
                      this.disableBurnCoup(burnCoupId);
                    }
                  };
                }
              });
            }
          });

          layer.addTo(this.map.map);
          
          // Add text label at the center of the polygon (only if zoom > 11)
          try {
            const bounds = layer.getBounds();
            const center = bounds.getCenter();
            
            // Always create the label, but only add it to map if zoom > 11
            const textLabel = L.marker(center, {
              icon: L.divIcon({
                  className: 'coup-label',
                  html: `<div style="font-weight: bold; color: #000; text-shadow: 1px 1px 2px #fff, -1px -1px 2px #fff, 1px -1px 2px #fff, -1px 1px 2px #fff; font-size: 14px; white-space: nowrap;">${(coup.identifier || 'Coup #' + coup.id).toUpperCase()}</div>`,
                  iconSize: [0, 0]
                }),
                interactive: false
              });
            
            // Only add to map if zoom level is greater than 11
            if (this.currentZoomLevel > 12) {
              textLabel.addTo(this.map.map);
            }
            
            // Store both the polygon layer and the label together
            this.coupLayers[coupId] = { polygon: layer, label: textLabel };
          } catch (error) {
            console.error(`Failed to add label for coup ${coupId}:`, error);
            this.coupLayers[coupId] = { polygon: layer, label: null };
          }
        } catch (error) {
          console.error(`Failed to parse geometry for coup ${coup.id}:`, error);
          console.error('Geometry data:', coup.geometry);
          console.error('Geometry type:', typeof coup.geometry);
        }
      }
    });

    // Fit bounds only on initial load, not on zoom/pan updates
    if (this.shouldFitBounds && this.allCoups.length > 0 && Object.keys(this.coupLayers).length > 0) {
      this.isInitialLoad = true; // Prevent event listeners from triggering during fit
      const polygonLayers = Object.values(this.coupLayers).map((item: any) => item.polygon).filter(p => p);
      const group = L.featureGroup(polygonLayers);
      this.map.map.fitBounds(group.getBounds(), { padding: [50, 50] });
      this.shouldFitBounds = false; // Reset flag after fitting
      
      // Re-enable event listeners after a short delay (after fitBounds completes)
      setTimeout(() => {
        this.isInitialLoad = false;
        console.log('Initial load complete - event listeners re-enabled');
      }, 100);
    }

    // Add air quality markers (they don't affect the bounds)
    this.addAirQualityMarkers();
    this.addFuelMoistureSensorMarkers();
    this.addWeatherStationMarkers();
  }

  private createCoupPopupContent(coup: any, isSelected: boolean, burnCoup?: any): HTMLElement {
    const container = document.createElement('div');
    container.style.minWidth = '200px';
    
    const isDisabled = burnCoup?.disabled || false;
    
    // Get fuel moisture data using burnCoup.id if available
    const shadedValue = burnCoup ? this.getFuelMoistureShadedValue(burnCoup.id) : null;
    const unshadedValue = burnCoup ? this.getFuelMoistureUnshadedValue(burnCoup.id) : null;
    const shadedClass = shadedValue !== null ? this.getFuelMoistureBadgeClassWithStaleCheck(burnCoup.id, shadedValue, true) : null;
    const unshadedClass = unshadedValue !== null ? this.getFuelMoistureBadgeClassWithStaleCheck(burnCoup.id, unshadedValue, false) : null;
    
    // Convert badge classes to colors for popup
    const getColorFromClass = (badgeClass: string | null): string => {
      if (badgeClass === 'bg-danger') return '#dc3545';
      if (badgeClass === 'bg-warning') return '#ffc107';
      if (badgeClass === 'bg-success') return '#28a745';
      if (badgeClass === 'bg-light') return '#f8f9fa';
      return '#6c757d'; // secondary
    };
    
    container.innerHTML = `
      <div style="margin-bottom: 10px;">
        <b style="font-size: 14px; color: black;">${coup.identifier || 'Unnamed Coup'}</b><br>
        <span style="color: black;">Name: ${coup.name}</span><br>
        ${coup.description ? '<span style="color: black;">' + coup.description + '</span><br>' : ''}
      </div>
      <div style="margin-bottom: 10px;">
        ${isSelected 
          ? `<span style="color: ${isDisabled ? '#dc3545' : '#ff7800'}; font-weight: bold;">
              <i class="bi bi-check-circle"></i> SELECTED FOR BURN
              ${isDisabled ? '<br><span style="font-size: 12px; color: #dc3545;"><i class="bi bi-slash-circle"></i> DISABLED</span>' : ''}
             </span>` 
          : '<span style="color: #999;"><i class="bi bi-circle"></i> Available</span>'}
      </div>
      ${isSelected && (shadedValue !== null || unshadedValue !== null) ? `
        <div style="margin-bottom: 10px; padding: 8px; background-color: #f8f9fa; border-radius: 4px;">
          <small style="font-weight: bold; color: #495057;"><i class="bi bi-droplet-fill"></i> Fuel Moisture</small><br>
          ${shadedValue !== null ? `
            <span style="display: inline-block; margin-top: 4px; padding: 2px 6px; background-color: ${getColorFromClass(shadedClass)}; color: ${shadedClass === 'bg-light' ? '#212529' : 'white'}; border-radius: 3px; font-size: 12px;">
              <i class="bi bi-droplet-fill"></i> Shade: ${shadedValue}%
            </span>
          ` : ''}
          ${unshadedValue !== null ? `
            <span style="display: inline-block; margin-top: 4px; margin-left: 4px; padding: 2px 6px; background-color: ${getColorFromClass(unshadedClass)}; color: ${unshadedClass === 'bg-light' ? '#212529' : 'white'}; border-radius: 3px; font-size: 12px;">
              <i class="bi bi-droplet-fill"></i> Open: ${unshadedValue}%
            </span>
          ` : ''}
        </div>
      ` : ''}
    `;
    
    if (this.selectedBurn) {
      const coupId = coup.id;
      
      // Add/Remove button
      const toggleButton = document.createElement('button');
      toggleButton.id = `coup-toggle-${coupId}`;
      toggleButton.className = isSelected ? 'btn btn-sm btn-outline-danger w-100' : 'btn btn-sm btn-primary w-100';
      toggleButton.innerHTML = isSelected 
        ? '<i class="bi bi-dash-circle"></i> Remove from Burn Plan' 
        : '<i class="bi bi-plus-circle"></i> Add to Burn Plan';
      toggleButton.style.marginTop = '5px';
      container.appendChild(toggleButton);
      
      // Enable/Disable button (only show if coup is selected)
      if (isSelected && burnCoup) {
        const enableDisableButton = document.createElement('button');
        enableDisableButton.id = `coup-enable-disable-${coupId}`;
        enableDisableButton.setAttribute('data-burn-coup-id', burnCoup.id.toString());
        enableDisableButton.className = isDisabled 
          ? 'btn btn-sm btn-success w-100' 
          : 'btn btn-sm btn-warning w-100';
        enableDisableButton.innerHTML = isDisabled 
          ? '<i class="bi bi-check-circle"></i> Enable Coup' 
          : '<i class="bi bi-slash-circle"></i> Disable Coup';
        enableDisableButton.style.marginTop = '5px';
        container.appendChild(enableDisableButton);
      }
    }
    
    return container;
  }

  toggleCoupInBurnPlan(geoAssetId: number): void {
    if (!this.selectedBurn) {
      this.toasterService.warning('Please select a burn plan first');
      return;
    }

    const isCurrentlySelected = this.selectedBurn.burnCoups?.some(bc => bc.geoAsset.id === geoAssetId) || false;
    
    if (isCurrentlySelected) {
      this.removeCoupFromBurnPlan(geoAssetId);
    } else {
      this.addCoupToBurnPlan(geoAssetId);
    }
  }

  addCoupToBurnPlan(geoAssetId: number): void {
    this.loading = true;
    
    this.burnListService.BurnListAddBurnCoup({
      customerId: this.app.customerId,
      burnListId: this.selectedBurn.id,
      geoAssetId: geoAssetId
    }).subscribe(
      (updatedBurnPlan) => {
        // Clear cache to ensure fresh data on next load
        this.burnListDataService.clearBurnListsCache(this.app.customerId);
        this.selectedBurn = updatedBurnPlan;
        this.selectedBurn.burnCoups = this.selectedBurn.burnCoups || [];
        this.toasterService.success('Coup added to burn plan');
        this.loading = false;
        this.displayCoupsOnMap();
        // Refresh fuel moisture sensor markers to update button visibility
        this.addFuelMoistureSensorMarkers();
      },
      (error) => {
        console.error('Failed to add coup to burn plan', error);
        this.toasterService.error('Failed to add coup to burn plan');
        this.loading = false;
      }
    );
  }

  addCoupBySiteUri(siteUri: string): void {
    if (!this.selectedBurn) {
      this.toasterService.warning('Please select a burn plan first');
      return;
    }
    
    if (!this.coupTypeId) {
      this.toasterService.error('Coup type not configured. Cannot add coup.');
      return;
    }
    
    if (!siteUri) {
      this.toasterService.error('No coup identifier provided');
      return;
    }

    this.loading = true;
    
    // Convert siteUri to uppercase for matching
    const normalizedSiteUri = siteUri.trim().toUpperCase();
    
    // Search for the coup by siteUri
    this.geoAssetService.GeoAssetSearch({
      customerId: this.app.customerId,
      type: this.coupTypeId,
      searchSize: 10,
      pageIndex: 0,
      search: normalizedSiteUri
    }).subscribe(
      (results) => {
        if (!results || results.length === 0) {
          this.toasterService.error(`Coup "${siteUri}" not found`);
          this.loading = false;
          return;
        }
        
        // Find exact match by identifier or name (case-insensitive)
        const coup = results.find(r => 
          r.identifier?.toUpperCase() === normalizedSiteUri || 
          r.name?.toUpperCase() === normalizedSiteUri
        );
        if (!coup) {
          this.toasterService.error(`Coup "${siteUri}" not found`);
          this.loading = false;
          return;
        }
        
        // Check if coup is already in the burn plan
        if (this.isCoupAlreadyInBurnPlan(coup.id)) {
          this.toasterService.info(`Coup "${siteUri}" is already in this burn plan`);
          this.loading = false;
          return;
        }
        
        // Add the coup to the burn plan
        this.addCoupToBurnPlan(coup.id);
      },
      (error) => {
        console.error('Failed to search for coup', error);
        this.toasterService.error('Failed to search for coup');
        this.loading = false;
      }
    );
  }

  removeCoupFromBurnPlanByCoup(coup: any): void {
    debugger;
    if (!coup || !coup.geoAsset) {
      this.toasterService.error('Invalid coup selected for removal');
      return;
    }
    this.removeCoupFromBurnPlan(coup.geoAsset.id);
  }

  removeCoupFromBurnPlan(geoAssetId: number): void {
    this.loading = true;
    
    this.burnListService.BurnListRemoveBurnCoup({
      customerId: this.app.customerId,
      burnListId: this.selectedBurn.id,
      geoAssetId: geoAssetId
    }).subscribe(
      (updatedBurnPlan) => {
        // Clear cache to ensure fresh data on next load
        this.burnListDataService.clearBurnListsCache(this.app.customerId);
        this.selectedBurn = updatedBurnPlan;
        this.selectedBurn.burnCoups = this.selectedBurn.burnCoups || [];
        this.loading = false;
        this.toasterService.success('Coup removed from burn plan');
        this.displayCoupsOnMap();
        // Refresh fuel moisture sensor markers to update button visibility
        this.addFuelMoistureSensorMarkers();
      },
      (error) => {
        console.error('Failed to remove coup from burn plan', error);
        this.toasterService.error('Failed to remove coup from burn plan');
        this.loading = false;
      }
    );
  }

  openAddCoupModal(): void {
    if (!this.selectedBurn) {
      this.toasterService.warning('Please select a burn plan first');
      return;
    }
    
    if (!this.coupTypeId) {
      this.toasterService.error('Coup type not configured. Cannot search for coups.');
      return;
    }
    
    // Reset search state
    this.coupSearchQuery = '';
    this.coupSearchResults = [];
    this.coupSearching = false;
    
    // Open the modal
    this.addCoupModal.show();
  }

  onCoupSearchQueryChange(): void {
    // Clear previous timer
    if (this.coupSearchDebounceTimer) {
      clearTimeout(this.coupSearchDebounceTimer);
    }
    
    // If query is empty, clear results
    if (!this.coupSearchQuery || this.coupSearchQuery.trim().length === 0) {
      this.coupSearchResults = [];
      return;
    }
    
    // Debounce search by 300ms
    this.coupSearchDebounceTimer = setTimeout(() => {
      this.searchCoups();
    }, 300);
  }

  searchCoups(): void {
    if (!this.coupSearchQuery || this.coupSearchQuery.trim().length === 0) {
      return;
    }
    
    this.coupSearching = true;
    
    this.geoAssetService.GeoAssetSearch({
      customerId: this.app.customerId,
      type: this.coupTypeId,
      searchSize: 50,
      pageIndex: 0,
      search: this.coupSearchQuery.trim()
    }).subscribe(
      (results) => {
        this.coupSearchResults = results;
        this.coupSearching = false;
      },
      (error) => {
        console.error('Failed to search coups', error);
        this.toasterService.error('Failed to search for coups');
        this.coupSearching = false;
        this.coupSearchResults = [];
      }
    );
  }

  selectCoupFromSearch(coup: GeoAssetDetailDTO): void {
    // Close the modal
    this.addCoupModal.hide();
    
    // Add the coup to the burn plan
    this.addCoupToBurnPlan(coup.id);
  }

  isCoupAlreadyInBurnPlan(coupId: number): boolean {
    return this.selectedBurn?.burnCoups?.some(bc => bc.geoAsset.id === coupId) || false;
  }

  isCoupWithSiteUriInBurnPlan(siteUri: string): boolean {
    if (!this.selectedBurn || !this.selectedBurn.burnCoups || !siteUri) {
      return false;
    }
    const normalizedSiteUri = siteUri.trim().toUpperCase();
    return this.selectedBurn.burnCoups.some(bc => 
      bc.geoAsset?.identifier?.toUpperCase() === normalizedSiteUri || 
      bc.geoAsset?.name?.toUpperCase() === normalizedSiteUri
    );
  }

  enableBurnCoup(burnCoupId: number): void {
    this.loading = true;
    
    this.burnListService.BurnListEnableBurnCoup({
      customerId: this.app.customerId,
      burnCoupId: burnCoupId
    }).subscribe(
      (updatedBurnCoup) => {
        // Clear cache to ensure fresh data on next load
        this.burnListDataService.clearBurnListsCache(this.app.customerId);
        // Update the burn coup in the selected burn plan
        const index = this.selectedBurn.burnCoups?.findIndex(bc => bc.id === burnCoupId);
        if (index !== -1 && this.selectedBurn.burnCoups) {
          this.selectedBurn.burnCoups[index] = updatedBurnCoup;
        }
        this.loading = false;
        this.toasterService.success('Coup enabled');
        this.displayCoupsOnMap();
      },
      (error) => {
        console.error('Failed to enable coup', error);
        this.toasterService.error('Failed to enable coup');
        this.loading = false;
      }
    );
  }

  disableBurnCoup(burnCoupId: number): void {
    this.loading = true;
    
    this.burnListService.BurnListDisableBurnCoup({
      customerId: this.app.customerId,
      burnCoupId: burnCoupId
    }).subscribe(
      (updatedBurnCoup) => {
        // Clear cache to ensure fresh data on next load
        this.burnListDataService.clearBurnListsCache(this.app.customerId);
        // Update the burn coup in the selected burn plan
        const index = this.selectedBurn.burnCoups?.findIndex(bc => bc.id === burnCoupId);
        if (index !== -1 && this.selectedBurn.burnCoups) {
          this.selectedBurn.burnCoups[index] = updatedBurnCoup;
        }
        this.loading = false;
        this.toasterService.success('Coup disabled');
        this.displayCoupsOnMap();
      },
      (error) => {
        console.error('Failed to disable coup', error);
        this.toasterService.error('Failed to disable coup');
        this.loading = false;
      }
    );
  }

  updateBurnPlanStatus(statusId: number): void {
    if (!this.selectedBurn) {
      return;
    }

    this.loading = true;
    
    this.burnListService.BurnListUpdateBurnListStatus({
      customerId: this.app.customerId,
      burnListId: this.selectedBurn.id,
      burnListStatusId: statusId
    }).subscribe(
      (result) => {
        // Clear cache to ensure fresh data on next load
        this.burnListDataService.clearBurnListsCache(this.app.customerId);
        this.selectedBurn.burnListStatusId = result.burnListStatusId;
        // Update the status in the plannedBurns array as well
        const index = this.plannedBurns.findIndex(bp => bp.id === result.id);
        if (index !== -1) {
          this.plannedBurns[index].burnListStatusId = result.burnListStatusId;
        }
        this.loading = false;
        this.toasterService.success('Burn plan status updated');
      },
      (error) => {
        console.error('Failed to update burn plan status', error);
        this.toasterService.error('Failed to update burn plan status');
        this.loading = false;
      }
    );
  }

  getBurnPlanStatusName(statusId: number | undefined): string {
    if (!statusId) {
      return 'Not Set';
    }
    const status = this.burnListStatuses.find(s => s.id === statusId);
    return status ? status.name : 'Unknown';
  }

  getBurnListRegionName(regionId: number | undefined): string {
    if (!regionId) {
      return 'Not Set';
    }
    const region = this.burnListRegions.find(r => r.id === regionId);
    return region ? region.name : 'Unknown';
  }

  isPlannedStatus(): boolean {
    if (!this.selectedBurn || !this.selectedBurn.burnListStatusId) {
      return false;
    }
    const status = this.burnListStatuses.find(s => s.id === this.selectedBurn!.burnListStatusId);
    if (!status) return false;
    return status.name.toLowerCase().includes('plan');
  }

  getInProgressStatusId(): number | null {
    const status = this.burnListStatuses.find(s => s.name.toLowerCase().includes('progress'));
    return status ? status.id : null;
  }

  startBurnPlan(): void {
    if (!this.selectedBurn) return;
    
    const inProgressStatusId = this.getInProgressStatusId();
    if (!inProgressStatusId) {
      this.toasterService.error('In Progress status not found');
      return;
    }
    
    this.updateBurnPlanStatus(inProgressStatusId);
  }

  isInProgressStatus(): boolean {
    if (!this.selectedBurn || !this.selectedBurn.burnListStatusId) {
      return false;
    }
    const status = this.burnListStatuses.find(s => s.id === this.selectedBurn!.burnListStatusId);
    if (!status) return false;
    return status.name.toLowerCase().includes('progress');
  }

  startBurnCoup(coup: any): void {
    if (!coup) return;
    
    this.loading = true;
    this.burnListService.BurnListStartBurn({
      customerId: this.app.customerId,
      burnCoupId: coup.id
    }).subscribe(
      (result) => {
        // Update the burnStartedTime in the local coup
        coup.burnStartedTime = result.burnStartedTime;
        this.toasterService.success('Burn started successfully');
        this.loading = false;
      },
      (error) => {
        console.error('Failed to start burn', error);
        this.toasterService.error('Failed to start burn');
        this.loading = false;
      }
    );
  }

  finishBurnCoup(coup: any): void {
    if (!coup) return;
    
    this.loading = true;
    this.burnListService.BurnListFinishBurn({
      customerId: this.app.customerId,
      burnCoupId: coup.id
    }).subscribe(
      (result) => {
        // Update the burnCompletedTime in the local coup
        coup.burnCompletedTime = result.burnCompletedTime;
        this.toasterService.success('Burn completed successfully');
        this.loading = false;
      },
      (error) => {
        console.error('Failed to complete burn', error);
        this.toasterService.error('Failed to complete burn');
        this.loading = false;
      }
    );
  }

  areAllBurnCoupsCompleted(): boolean {
    if (!this.selectedBurn || !this.selectedBurn.burnCoups || this.selectedBurn.burnCoups.length === 0) {
      return false;
    }
    
    // Get only enabled coups
    const enabledCoups = this.selectedBurn.burnCoups.filter(coup => !coup.disabled);
    if (enabledCoups.length === 0) {
      return false;
    }
    
    // Check if all enabled coups have a burnCompletedTime
    return enabledCoups.every(coup => coup.burnCompletedTime);
  }

  getCompletedStatusId(): number | null {
    const status = this.burnListStatuses.find(s => s.name.toLowerCase().includes('complet'));
    return status ? status.id : null;
  }

  finishBurnPlan(): void {
    if (!this.selectedBurn) return;
    
    const completedStatusId = this.getCompletedStatusId();
    if (!completedStatusId) {
      this.toasterService.error('Completed status not found');
      return;
    }
    
    this.updateBurnPlanStatus(completedStatusId);
  }

  // EPA Air Quality methods
  startAirQualityRefreshTimer(): void {
    // Load immediately
    this.loadAirQualitySites();
    this.loadFuelMoistureSensorSites();
    this.loadWeatherStationSites();
    
    // Then refresh every 5 minutes
    this.airQualityRefreshInterval = setInterval(() => {
      this.loadAirQualitySites();
      this.loadFuelMoistureSensorSites();
      this.loadWeatherStationSites();
    }, this.AIR_QUALITY_REFRESH_MS);
  }

  loadAirQualitySites(): void {
    if (!this.app.customerId) {
      console.warn('No customer ID available for loading air quality sites');
      return;
    }

    this.airQualitySitesLoading = true;
    const siteTypeCode = 'EPA Air Quality';

    this.siteTypeService.SiteTypeSearch({
      customerId: this.app.customerId,
      type: siteTypeCode,
      searchSize: 1000,
      pageIndex: 0,
      filterOld: false
    }).subscribe(
      (sites) => {
        this.airQualitySites = sites || [];
        this.airQualitySitesLoading = false;
        console.log(`Loaded ${this.airQualitySites.length} EPA Air Quality sites`, this.airQualitySites);
        
        // Add markers to map if map is ready
        if (this.map && this.map.map) {
          this.addAirQualityMarkers();
        }
      },
      (error) => {
        console.error('Failed to load EPA Air Quality sites', error);
        this.airQualitySitesLoading = false;
      }
    );
  }

  addAirQualityMarkers(): void {
    if (!this.map || !this.map.map || this.airQualitySites.length === 0) {
      return;
    }

    // Remove existing markers first
    this.removeAirQualityMarkers();

    this.airQualitySites.forEach(site => {
      if (site.latitude && site.longitude) {
        const color = this.getAirQualityMarkerColor(site);

        const marker = L.circleMarker([site.latitude, site.longitude], {
          radius: 8,
          fillColor: color,
          color: '#fff',
          weight: 2,
          opacity: 1,
          fillOpacity: 0.8
        });

        const popupContent = this.createAirQualityPopupContent(site);
        marker.bindPopup(popupContent);
        marker.addTo(this.map.map);
        
        this.airQualityMarkers.push(marker);
      }
    });

    console.log(`Added ${this.airQualityMarkers.length} air quality markers to map`);
  }

  removeAirQualityMarkers(): void {
    if (!this.map || !this.map.map) {
      return;
    }

    this.airQualityMarkers.forEach(marker => {
      this.map.map.removeLayer(marker);
    });
    this.airQualityMarkers = [];
  }

  // Fuel Moisture Sensor methods
  loadFuelMoistureSensorSites(): void {
    if (!this.app.customerId) {
      console.warn('No customer ID available for loading fuel moisture sensor sites');
      return;
    }

    this.fuelMoistureSensorSitesLoading = true;
    const siteTypeCode = 'Fuel Moisture';

    this.siteTypeService.SiteTypeGetAll(this.app.customerId).subscribe(types =>{
      this.siteTypes = types;
      console.log('Available site types:', types);

      const fm = types.find(t => t.siteTypeCode === siteTypeCode);
      this.siteTypeService.SiteTypeGetById({customerId: this.app.customerId, siteTypeId: fm.siteTypeId}).subscribe(siteType => {
        this.fuelMoistureSiteType = siteType;
      });

      const aq = types.find(t => t.siteTypeCode === "EPA Air Quality");
      this.siteTypeService.SiteTypeGetById({customerId: this.app.customerId, siteTypeId: aq.siteTypeId}).subscribe(siteType => {
        this.airQualitySiteType = siteType;
      });

      const ws = types.find(t => t.siteTypeCode === "Weather Station");
      this.siteTypeService.SiteTypeGetById({customerId: this.app.customerId, siteTypeId: ws.siteTypeId}).subscribe(siteType => {
        this.weatherStationSiteType = siteType;
      });
    });

    this.siteTypeService.SiteTypeSearch({
      customerId: this.app.customerId,
      type: siteTypeCode,
      searchSize: 1000,
      pageIndex: 0,
      filterOld: false
    }).subscribe(
      (sites) => {
        this.fuelMoistureSensorSites = sites || [];
        this.fuelMoistureSensorSitesLoading = false;
        console.log(`Loaded ${this.fuelMoistureSensorSites.length} Fuel Moisture sensor sites`, this.fuelMoistureSensorSites);
        
        // Add markers to map if map is ready
        if (this.map && this.map.map) {
          this.addFuelMoistureSensorMarkers();
        }
      },
      (error) => {
        console.error('Failed to load Fuel Moisture sensor sites', error);
        this.fuelMoistureSensorSitesLoading = false;
      }
    );
  }

  addFuelMoistureSensorMarkers(): void {
    if (!this.map || !this.map.map || this.fuelMoistureSensorSites.length === 0) {
      return;
    }

    // Remove existing markers first
    this.removeFuelMoistureSensorMarkers();

    this.fuelMoistureSensorSites.forEach(site => {
      if (site.latitude && site.longitude) {
        // Create a marker with fuel-moisture.png icon
        const marker = L.marker([site.latitude, site.longitude], {
          icon: L.icon({
            iconUrl: 'assets/icons/fuel-moisture.png',
            iconSize: [25, 41],
            iconAnchor: [12, 41],
            popupAnchor: [1, -34]
          })
        });

        // Extract fuel moisture data from site
        const shadedValue = this.getSiteFuelMoistureValue(site, true);
        const unshadedValue = this.getSiteFuelMoistureValue(site, false);
        
        // Create popup content with site name, coup, and fuel moisture data
        let popupContent = `
          <div style="min-width: 200px;">
            <h6 style="margin-bottom: 5px; font-weight: bold; color: black;">${site.siteName || 'Unknown Sensor'}</h6>
            <div style="margin-bottom: 8px;">
              <strong style="color: #555;">Coup:</strong> <span style="color: #333;">${site.siteUri || 'N/A'}</span>
            </div>
        `;
        
        // Add fuel moisture data if available
        if (shadedValue !== null || unshadedValue !== null) {
          popupContent += '<div style="margin-top: 10px; border-top: 1px solid #ddd; padding-top: 8px;">';
          popupContent += '<strong style="color: #555;">Fuel Moisture:</strong>';
          
          if (shadedValue !== null) {
            const shadedClass = this.getFuelMoistureBadgeClass(shadedValue, true);
            const shadedBgColor = this.getBadgeColorFromClass(shadedClass);
            popupContent += `
              <div style="margin-top: 5px;">
                <span style="font-size: 12px; color: #666;">Shaded:</span>
                <span style="display: inline-block; padding: 2px 8px; border-radius: 4px; background-color: ${shadedBgColor}; color: white; font-weight: bold; margin-left: 5px;">${shadedValue}%</span>
              </div>
            `;
          }
          
          if (unshadedValue !== null) {
            const unshadedClass = this.getFuelMoistureBadgeClass(unshadedValue, false);
            const unshadedBgColor = this.getBadgeColorFromClass(unshadedClass);
            popupContent += `
              <div style="margin-top: 5px;">
                <span style="font-size: 12px; color: #666;">Unshaded:</span>
                <span style="display: inline-block; padding: 2px 8px; border-radius: 4px; background-color: ${unshadedBgColor}; color: white; font-weight: bold; margin-left: 5px;">${unshadedValue}%</span>
              </div>
            `;
          }
          
          popupContent += '</div>';
        }
        
        // Add button to add coup to burn list if a burn list is selected and coup is not already in the plan
        if (this.selectedBurn && site.siteUri && !this.isCoupWithSiteUriInBurnPlan(site.siteUri)) {
          popupContent += `
            <div style="margin-top: 12px; border-top: 1px solid #ddd; padding-top: 8px;">
              <button 
                id="add-coup-btn-${site.siteUri.replace(/[^a-zA-Z0-9]/g, '-')}" 
                class="btn btn-sm btn-primary" 
                style="width: 100%; font-size: 12px;"
              >
                <i class="bi bi-plus-circle"></i> Add Coup to Burn List
              </button>
            </div>
          `;
        }
        
        popupContent += '</div>';
        
        marker.bindPopup(popupContent);
        
        // Add event listener when popup opens
        marker.on('popupopen', () => {
          if (this.selectedBurn && site.siteUri) {
            const btnId = `add-coup-btn-${site.siteUri.replace(/[^a-zA-Z0-9]/g, '-')}`;
            const button = document.getElementById(btnId);
            if (button) {
              button.addEventListener('click', () => {
                this.addCoupBySiteUri(site.siteUri);
              });
            }
          }
        });
        
        marker.addTo(this.map.map);
        
        this.fuelMoistureSensorMarkers.push(marker);
      }
    });

    console.log(`Added ${this.fuelMoistureSensorMarkers.length} fuel moisture sensor markers to map`);
  }

  removeFuelMoistureSensorMarkers(): void {
    if (!this.map || !this.map.map) {
      return;
    }

    this.fuelMoistureSensorMarkers.forEach(marker => {
      this.map.map.removeLayer(marker);
    });
    this.fuelMoistureSensorMarkers = [];
  }

  // Weather Station methods
  loadWeatherStationSites(): void {
    if (!this.app.customerId) {
      console.warn('No customer ID available for loading weather stations');
      return;
    }

    this.weatherStationSitesLoading = true;
    const siteTypeCode = 'Weather Station';

    this.siteTypeService.SiteTypeSearch({
      customerId: this.app.customerId,
      type: siteTypeCode,
      searchSize: 1000,
      pageIndex: 0,
      filterOld: false
    }).subscribe(
      (sites) => {
        this.weatherStationSites = sites || [];
        this.weatherStationSitesLoading = false;
        console.log(`Loaded ${this.weatherStationSites.length} Weather Station sites`, this.weatherStationSites);
        
        // Add markers to map if map is ready
        if (this.map && this.map.map) {
          this.addWeatherStationMarkers();
        }
      },
      (error) => {
        console.error('Failed to load Weather Station sites', error);
        this.weatherStationSitesLoading = false;
      }
    );
  }

  addWeatherStationMarkers(): void {
    if (!this.map || !this.map.map || this.weatherStationSites.length === 0) {
      return;
    }

    // Remove existing markers first
    this.removeWeatherStationMarkers();

    this.weatherStationSites.forEach(site => {
      if (site.latitude && site.longitude) {
        const windData = this.getWindData(site);
        const icon = this.createWindBarbIcon(site, windData);

        const marker = L.marker([site.latitude, site.longitude], { icon });

        const popupContent = this.createWeatherStationPopupContent(site, windData);
        marker.bindPopup(popupContent);
        marker.addTo(this.map.map);
        
        this.weatherStationMarkers.push(marker);
      }
    });

    console.log(`Added ${this.weatherStationMarkers.length} weather station markers to map`);
  }

  removeWeatherStationMarkers(): void {
    if (!this.map || !this.map.map) {
      return;
    }

    this.weatherStationMarkers.forEach(marker => {
      this.map.map.removeLayer(marker);
    });
    this.weatherStationMarkers = [];
  }

  getWindData(site: any): { speed: number, direction: number, temperature: number | null, humidity: number | null, age: number } {
    const onehour = 3600000 * 100;
    let speed = 0;
    let direction = 0;
    let temperature: number | null = null;
    let humidity: number | null = null;
    
    const time = new Date(site.latestTimeStamp);
    const now = new Date();
    const age = now.getTime() - time.getTime();

    if (site.parameters) {
      for (const key in site.parameters) {
        const param = site.parameters[key];
        
        if (param.unitType === 'Wind Speed' && param.latestValue) {
          const s = Number(param.latestValue.value);
          if (isNumber(s)) {
            speed = s;
          }
        }
        
        if (param.unitType === 'Wind Direction' && param.latestValue) {
          const d = Number(param.latestValue.value);
          if (isNumber(d) && d >= 0 && d <= 360) {
            direction = d;
          }
        }
        
        if (param.unitType === 'Air Temperature' && param.latestValue) {
          const t = Number(param.latestValue.value);
          if (isNumber(t)) {
            temperature = t;
          }
        }
        
        if ((param.unitType === 'Relative Humidity' || param.unitType === 'Humidity') && param.latestValue) {
          const h = Number(param.latestValue.value);
          if (isNumber(h)) {
            humidity = h;
          }
        }
      }
    }

    return { speed, direction, temperature, humidity, age };
  }

  createWindBarbIcon(site: any, windData: { speed: number, direction: number, temperature: number | null, humidity: number | null, age: number }): any {
    const onehour = 3600000 * 100;
    let fillColor = '#8f8f8f'; // Default gray for old data

    // If data is recent (less than 100 hours old)
    if (windData.age < onehour) {
      fillColor = '#2B85C7'; // Default blue
      
      // Color by temperature if available
      if (windData.temperature !== null) {
        if (windData.temperature > 30) {
          fillColor = '#F00'; // Red for hot
        } else if (windData.temperature > 20) {
          fillColor = '#ff7b00'; // Orange
        } else if (windData.temperature > 10) {
          fillColor = '#e5ff00'; // Yellow
        } else {
          fillColor = '#0044ff'; // Blue for cold
        }
      }
    }

    // Create wind barb icon
    if (windData.speed > 0 && windData.direction >= 0) {
      // Full wind barb with direction and speed
      return WindBarb.icon({
        lat: site.latitude,
        deg: windData.direction,
        speed: windData.speed,
        pointRadius: 6,
        strokeLength: 20,
        fillColor: fillColor
      });
    } else if (windData.temperature !== null) {
      // Temperature only, no wind data
      return WindBarb.icon({
        lat: site.latitude,
        deg: 0,
        speed: 0,
        pointRadius: 5,
        strokeLength: 20,
        fillColor: fillColor
      });
    } else {
      // No data, just a point
      return WindBarb.icon({
        lat: site.latitude,
        deg: 0,
        speed: 0,
        pointRadius: 3,
        strokeLength: 20,
        fillColor: '#0044dd'
      });
    }
  }

  createWeatherStationPopupContent(site: any, windData: { speed: number, direction: number, temperature: number | null, humidity: number | null, age: number }): string {
    const onehour = 3600000 * 100;
    const isRecent = windData.age < onehour;
    const siteDescription = site.description || site.siteDescription || '';
    
    let content = `
      <div style="min-width: 200px;">
        <h6 style="margin-bottom: 5px; font-weight: bold; color: black;">${site.siteName || 'Weather Station'}</h6>
        ${siteDescription ? `<div style="margin-bottom: 8px; color: black; font-size: 12px;">${siteDescription}</div>` : ''}
        <div style="margin-bottom: 5px;">
          <small style="color: #666;">Weather Station</small>
        </div>
    `;

    if (isRecent) {
      if (windData.speed > 0) {
        content += `
          <div style="margin-top: 8px; padding: 8px; background-color: #f8f9fa; border-radius: 4px;">
            <div style="margin-bottom: 4px;">
              <strong>Wind Speed:</strong> ${windData.speed.toFixed(1)} km/h
            </div>
            <div style="margin-bottom: 4px;">
              <strong>Wind Direction:</strong> ${windData.direction.toFixed(0)}°
            </div>
        `;
        
        if (windData.temperature !== null) {
          content += `
            <div style="margin-bottom: 4px;">
              <strong>Temperature:</strong> ${windData.temperature.toFixed(1)}°C
            </div>
          `;
        }
        
        if (windData.humidity !== null) {
          content += `
            <div>
              <strong>Humidity:</strong> ${windData.humidity.toFixed(1)}%
            </div>
          `;
        }
        
        content += `</div>`;
      } else if (windData.temperature !== null || windData.humidity !== null) {
        content += `
          <div style="margin-top: 8px; padding: 8px; background-color: #f8f9fa; border-radius: 4px;">
        `;
        
        if (windData.temperature !== null) {
          content += `
            <div style="margin-bottom: 4px;">
              <strong>Temperature:</strong> ${windData.temperature.toFixed(1)}°C
            </div>
          `;
        }
        
        if (windData.humidity !== null) {
          content += `
            <div>
              <strong>Humidity:</strong> ${windData.humidity.toFixed(1)}%
            </div>
          `;
        }
        
        content += `</div>`;
      } else {
        content += `
          <div style="margin-top: 8px; color: #999;">
            <small>No current data available</small>
          </div>
        `;
      }
    } else {
      content += `
        <div style="margin-top: 8px; color: #999;">
          <small>Data is outdated (last update: ${new Date(site.latestTimeStamp).toLocaleString()})</small>
        </div>
      `;
    }

    content += `</div>`;
    return content;
  }

  getPM10Value(site: any): number | null {
    if (!site.parameters) {
      return null;
    }
    
    // Search through all parameters to find one with unitType = "PM10"
    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'PM10 Ave') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }
    
    return null;
  }

  getPM25Value(site: any): number | null {
    if (!site.parameters) {
      return null;
    }
    
    // Search through all parameters to find one with unitType = "PM2.5"
    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'PM25 Ave') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }
    
    return null;
  }

  getPM10YesterdayValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }
    
    // Search through all parameters to find yesterday's PM10 average
    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && (parameter.unitType === 'PM10 Ave')) {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }
    
    return null;
  }

  getPM25YesterdayValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }
    
    // Search through all parameters to find yesterday's PM2.5 average
    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && (parameter.unitType === 'PM25 Ave')) {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }
    
    return null;
  }

  getPM25Color(pm25Value: number | null): string {
    if (pm25Value === null || pm25Value === undefined) {
      return 'bg-secondary'; // Grey for no data
    }

    if (pm25Value < 10) {
      return 'bg-success'; // Green (matches Fuel Moisture green)
    } else {
      return 'bg-danger'; // Red (matches Fuel Moisture red)
    }
  }

  getPM10Color(pm10Value: number | null): string {
    if (pm10Value === null || pm10Value === undefined) {
      return 'bg-secondary'; // Grey for no data
    }

    if (pm10Value < 50) {
      return 'bg-success'; // Green (matches Fuel Moisture green)
    } else {
      return 'bg-danger'; // Red (matches Fuel Moisture red)
    }
  }

  // Legacy method for backwards compatibility
  getAirQualityColor(value: number | null): string {
    return this.getPM10Color(value);
  }

  getAirQualityLevel(pm10Value: number | null): string {
    if (pm10Value === null || pm10Value === undefined) {
      return 'Unknown';
    }

    if (pm10Value < 10) {
      return 'Good';
    } else if (pm10Value < 25) {
      return 'Fairly Good';
    } else if (pm10Value < 50) {
      return 'Fairly Poor';
    } else if (pm10Value < 100) {
      return 'Poor';
    } else if (pm10Value < 300) {
      return 'Very Poor';
    } else {
      return 'Extremely Poor';
    }
  }

  getAirQualityColorForValue(value: number | null): string {
    return this.getAirQualityColor(value);
  }

  getAirQualityMarkerColor(site: any): string {
    const pm10YesterdayValue = this.getPM10YesterdayValue(site);
    const pm25YesterdayValue = this.getPM25YesterdayValue(site);
    
    const pm10YesterdayColor = this.getPM10Color(pm10YesterdayValue);
    const pm25YesterdayColor = this.getPM25Color(pm25YesterdayValue);
    
    // If either is red (danger), show red
    if (pm10YesterdayColor === 'bg-danger' || pm25YesterdayColor === 'bg-danger') {
      return '#dc3545'; // Red
    }
    
    // If both are green (success), show green
    if (pm10YesterdayColor === 'bg-success' && pm25YesterdayColor === 'bg-success') {
      return '#28a745'; // Green
    }
    
    // Otherwise show grey (no data or mixed)
    return '#6c757d'; // Grey
  }

  createAirQualityPopupContent(site: any): string {
    const pm10Value = this.getPM10Value(site);
    const pm25Value = this.getPM25Value(site);
    const pm10YesterdayValue = this.getPM10YesterdayValue(site);
    const pm25YesterdayValue = this.getPM25YesterdayValue(site);
    
    const pm10Color = this.getPM10Color(pm10Value);
    const pm25Color = this.getPM25Color(pm25Value);
    const pm10YesterdayColor = this.getPM10Color(pm10YesterdayValue);
    const pm25YesterdayColor = this.getPM25Color(pm25YesterdayValue);
    
    const pm10Level = this.getAirQualityLevel(pm10Value);
    const pm25Level = this.getAirQualityLevel(pm25Value);
    const pm10YesterdayLevel = this.getAirQualityLevel(pm10YesterdayValue);
    const pm25YesterdayLevel = this.getAirQualityLevel(pm25YesterdayValue);

    const siteName = site.siteName || 'Unknown Site';
    const siteDescription = site.description || site.siteDescription || '';
    const pm10Display = pm10Value !== null ? pm10Value.toFixed(2) : 'N/A';
    const pm25Display = pm25Value !== null ? pm25Value.toFixed(2) : 'N/A';
    const pm10YesterdayDisplay = pm10YesterdayValue !== null ? pm10YesterdayValue.toFixed(2) : 'N/A';
    const pm25YesterdayDisplay = pm25YesterdayValue !== null ? pm25YesterdayValue.toFixed(2) : 'N/A';

    // Get color classes for use in inline styles
    const getColorFromClass = (badgeClass: string): string => {
      if (badgeClass === 'bg-success') return '#28a745';
      if (badgeClass === 'bg-danger') return '#dc3545';
      if (badgeClass === 'bg-secondary') return '#6c757d';
      return '#6c757d';
    };

    return `
      <div style="min-width: 240px;">
        <h6 style="color:black;margin-bottom: 5px; font-weight: bold;">${siteName}</h6>
        ${siteDescription ? `<div style="margin-bottom: 10px; color: black; font-size: 12px;">${siteDescription}</div>` : ''}
        
        <div style="margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #ddd;">
          <div style="font-size: 11px; color: #666; font-weight: bold; margin-bottom: 4px; text-transform: uppercase;">Current</div>
          <div style="margin-bottom: 5px;">
            <strong style="color: black;">PM2.5:</strong> 
            <span style="color: ${getColorFromClass(pm25Color)}; font-weight: bold;">${pm25Display} µg/m³</span>
            <span style="color: #666; font-size: 11px; margin-left: 5px;">(${pm25Level})</span>
          </div>
          <div>
            <strong style="color: black;">PM10:</strong> 
            <span style="color: ${getColorFromClass(pm10Color)}; font-weight: bold;">${pm10Display} µg/m³</span>
            <span style="color: #666; font-size: 11px; margin-left: 5px;">(${pm10Level})</span>
          </div>
        </div>
        
        <div>
          <div style="font-size: 11px; color: #666; font-weight: bold; margin-bottom: 4px; text-transform: uppercase;">Yesterday Average</div>
          <div style="margin-bottom: 5px;">
            <strong style="color: black;">PM2.5:</strong> 
            <span style="color: ${getColorFromClass(pm25YesterdayColor)}; font-weight: bold;">${pm25YesterdayDisplay} µg/m³</span>
            <span style="color: #666; font-size: 11px; margin-left: 5px;">(${pm25YesterdayLevel})</span>
          </div>
          <div>
            <strong style="color: black;">PM10:</strong> 
            <span style="color: ${getColorFromClass(pm10YesterdayColor)}; font-weight: bold;">${pm10YesterdayDisplay} µg/m³</span>
            <span style="color: #666; font-size: 11px; margin-left: 5px;">(${pm10YesterdayLevel})</span>
          </div>
        </div>
      </div>
    `;
  }

  getGroupedBurnsByStatus(): { status: BurnListStatus | null, burns: BurnList[] }[] {
    const grouped: { status: BurnListStatus | null, burns: BurnList[] }[] = [];
    
    // Group burns with a status
    this.burnListStatuses.forEach(status => {
      const burnsForStatus = this.plannedBurns.filter(b => b.burnListStatusId === status.id);
      if (burnsForStatus.length > 0) {
        grouped.push({ status, burns: burnsForStatus });
      }
    });
    
    // Add burns without a status at the end
    const burnsWithoutStatus = this.plannedBurns.filter(b => !b.burnListStatusId);
    if (burnsWithoutStatus.length > 0) {
      grouped.push({ status: null, burns: burnsWithoutStatus });
    }
    
    return grouped;
  }

  getGroupedBurnsByType(): { type: BurnListStatus | null, burns: BurnList[] }[] {
    const grouped: { type: BurnListStatus | null, burns: BurnList[] }[] = [];
    
    // Group burns by type
    this.burnListTypes.forEach(type => {
      const burnsForType = this.plannedBurns.filter(b => b.burnListTypeId === type.id);
      if (burnsForType.length > 0) {
        grouped.push({ type, burns: burnsForType });
      }
    });
    
    // Add burns without a type at the end
    const burnsWithoutType = this.plannedBurns.filter(b => !b.burnListTypeId);
    if (burnsWithoutType.length > 0) {
      grouped.push({ type: null, burns: burnsWithoutType });
    }
    
    return grouped;
  }

  /**
   * Group burns first by region, then by type within each region
   */
  getGroupedBurnsByRegionAndType(): {
    region: BurnListRegion | null,
    typeGroups: { type: BurnListStatus | null, burns: BurnList[] }[]
  }[] {
    const grouped: {
      region: BurnListRegion | null,
      typeGroups: { type: BurnListStatus | null, burns: BurnList[] }[]
    }[] = [];
    
    // Group by region first
    this.burnListRegions.forEach(region => {
      const burnsForRegion = this.plannedBurns.filter(b => b.burnListRegionId === region.id);
      
      if (burnsForRegion.length > 0) {
        // Within this region, group by type
        const typeGroups: { type: BurnListStatus | null, burns: BurnList[] }[] = [];
        
        this.burnListTypes.forEach(type => {
          const burnsForType = burnsForRegion.filter(b => b.burnListTypeId === type.id);
          if (burnsForType.length > 0) {
            typeGroups.push({ type, burns: burnsForType });
          }
        });
        
        // Add burns without a type at the end
        const burnsWithoutType = burnsForRegion.filter(b => !b.burnListTypeId);
        if (burnsWithoutType.length > 0) {
          typeGroups.push({ type: null, burns: burnsWithoutType });
        }
        
        grouped.push({ region, typeGroups });
      }
    });
    
    // Add burns without a region at the end
    const burnsWithoutRegion = this.plannedBurns.filter(b => !b.burnListRegionId);
    if (burnsWithoutRegion.length > 0) {
      const typeGroups: { type: BurnListStatus | null, burns: BurnList[] }[] = [];
      
      this.burnListTypes.forEach(type => {
        const burnsForType = burnsWithoutRegion.filter(b => b.burnListTypeId === type.id);
        if (burnsForType.length > 0) {
          typeGroups.push({ type, burns: burnsForType });
        }
      });
      
      // Add burns without a type
      const burnsWithoutType = burnsWithoutRegion.filter(b => !b.burnListTypeId);
      if (burnsWithoutType.length > 0) {
        typeGroups.push({ type: null, burns: burnsWithoutType });
      }
      
      if (typeGroups.length > 0) {
        grouped.push({ region: null, typeGroups });
      }
    }
    
    return grouped;
  }

  /**
   * Toggle region accordion open/closed state
   */
  toggleRegion(regionId: number | null): void {
    if (regionId === null) {
      return; // Handle null region separately if needed
    }
    
    if (this.openRegionIds.has(regionId)) {
      this.openRegionIds.delete(regionId);
    } else {
      this.openRegionIds.add(regionId);
    }
    
    // Persist state to localStorage
    this.saveAccordionState();
  }

  /**
   * Check if region is expanded
   */
  isRegionOpen(regionId: number | null): boolean {
    if (regionId === null) {
      return true; // Default open for null region
    }
    return this.openRegionIds.has(regionId);
  }

  /**
   * Toggle type accordion open/closed state within a region
   */
  toggleType(regionId: number | null, typeId: number | null): void {
    if (regionId === null || typeId === null) {
      return; // Handle null cases separately if needed
    }
    
    if (!this.openTypeIds.has(regionId)) {
      this.openTypeIds.set(regionId, new Set());
    }
    
    const regionTypes = this.openTypeIds.get(regionId)!;
    if (regionTypes.has(typeId)) {
      regionTypes.delete(typeId);
    } else {
      regionTypes.add(typeId);
    }
    
    // Persist state to localStorage
    this.saveAccordionState();
  }

  /**
   * Check if type is expanded within a region
   */
  isTypeOpen(regionId: number | null, typeId: number | null): boolean {
    if (regionId === null || typeId === null) {
      return true; // Default open for null cases
    }
    
    if (!this.openTypeIds.has(regionId)) {
      return false;
    }
    
    return this.openTypeIds.get(regionId)!.has(typeId);
  }

  /**
   * Get total count of burns in a region group
   */
  getTotalBurnsInRegion(typeGroups: { type: BurnListStatus | null, burns: BurnList[] }[]): number {
    return typeGroups.reduce((sum, tg) => sum + tg.burns.length, 0);
  }

  /**
   * Toggle dashboard region accordion in menu
   */
  toggleDashboardRegion(regionId: number): void {
    if (this.openDashboardRegions.has(regionId)) {
      this.openDashboardRegions.delete(regionId);
    } else {
      this.openDashboardRegions.add(regionId);
    }
  }

  /**
   * Check if dashboard region is expanded in menu
   */
  isDashboardRegionOpen(regionId: number): boolean {
    return this.openDashboardRegions.has(regionId);
  }

  /**
   * Save accordion state to localStorage
   */
  private saveAccordionState(): void {
    try {
      const state = {
        openRegionIds: Array.from(this.openRegionIds),
        openTypeIds: Array.from(this.openTypeIds.entries()).map(([regionId, typeIds]) => ({
          regionId,
          typeIds: Array.from(typeIds)
        }))
      };
      localStorage.setItem('burnListAccordionState', JSON.stringify(state));
    } catch (error) {
      console.error('Failed to save accordion state to localStorage', error);
    }
  }

  /**
   * Load accordion state from localStorage
   */
  private loadAccordionState(): void {
    try {
      const savedState = localStorage.getItem('burnListAccordionState');
      if (savedState) {
        const state = JSON.parse(savedState);
        
        // Restore openRegionIds
        if (state.openRegionIds && Array.isArray(state.openRegionIds)) {
          this.openRegionIds = new Set(state.openRegionIds);
        }
        
        // Restore openTypeIds
        if (state.openTypeIds && Array.isArray(state.openTypeIds)) {
          this.openTypeIds = new Map();
          state.openTypeIds.forEach((item: { regionId: number, typeIds: number[] }) => {
            this.openTypeIds.set(item.regionId, new Set(item.typeIds));
          });
        }
        
        console.log('Loaded accordion state from localStorage', state);
      }
    } catch (error) {
      console.error('Failed to load accordion state from localStorage', error);
    }
  }

  getStatusBadgeClass(statusId: number | undefined): string {
    if (!statusId) return 'bg-secondary';
    switch (statusId) {
      case 1: return 'bg-primary';
      case 2: return 'bg-warning';
      case 3: return 'bg-success';
      case 4: return 'bg-secondary';
      case 5: return 'bg-info';
      default: return 'bg-secondary';
    }
  }

  getStatusIconClass(statusId: number | undefined): string {
    if (!statusId) return 'bg-gradient-secondary';
    switch (statusId) {
      case 1: return 'bg-gradient-primary';
      case 2: return 'bg-gradient-warning';
      case 3: return 'bg-gradient-success';
      case 4: return 'bg-gradient-secondary';
      case 5: return 'bg-gradient-info';
      default: return 'bg-gradient-secondary';
    }
  }

  getStatusIconColorClass(statusId: number | undefined): string {
    if (!statusId) return 'bg-secondary';
    
    const status = this.burnListStatuses.find(s => s.id === statusId);
    if (!status) return 'bg-secondary';
    
    const statusName = status.name.toLowerCase();
    
    if (statusName.includes('completed') || statusName.includes('finished')) {
      return 'bg-success';
    } else if (statusName.includes('progress') || statusName.includes('active') || statusName.includes('start')) {
      return 'bg-warning';
    } else if (statusName.includes('planned') || statusName.includes('scheduled')) {
      return 'bg-info';
    } else if (statusName.includes('cancelled') || statusName.includes('abandoned')) {
      return 'bg-danger';
    }
    
    return 'bg-secondary';
  }

  getActiveBurns(): BurnList[] {
    // Return burns that are "Planned" or "In Progress" based on status
    // Assuming status IDs: 1 = Planned, 2 = In Progress
    return this.plannedBurns.filter(burn => {
      const status = this.burnListStatuses.find(s => s.id === burn.burnListStatusId);
      if (!status) return false;
      // Check if status name contains "plan" or "progress" (case insensitive)
      const statusName = status.name.toLowerCase();
      return statusName.includes('plan') || statusName.includes('progress');
    });
  }

  getEnabledCoupsForBurn(burn: BurnList): any[] {
    if (!burn.burnCoups) return [];
    return burn.burnCoups.filter(coup => !coup.disabled);
  }

  getBurnPlanOverallFuelMoistureColor(burn: BurnList): string {
    const enabledCoups = this.getEnabledCoupsForBurn(burn);
    if (enabledCoups.length === 0) {
      return 'bg-secondary'; // No coups
    }

    let hasRed = false;
    let hasOrange = false;
    let hasGreen = false;
    let hasData = false;

    // Check all fuel moisture values for all enabled coups
    enabledCoups.forEach(coup => {
      const shadedValue = this.getFuelMoistureShadedValue(coup.id);
      const unshadedValue = this.getFuelMoistureUnshadedValue(coup.id);

      if (shadedValue !== null) {
        hasData = true;
        const shadedClass = this.getFuelMoistureBadgeClass(shadedValue, true);
        if (shadedClass === 'bg-danger') hasRed = true;
        else if (shadedClass === 'bg-warning') hasOrange = true;
        else if (shadedClass === 'bg-success') hasGreen = true;
      }

      if (unshadedValue !== null) {
        hasData = true;
        const unshadedClass = this.getFuelMoistureBadgeClass(unshadedValue, false);
        if (unshadedClass === 'bg-danger') hasRed = true;
        else if (unshadedClass === 'bg-warning') hasOrange = true;
        else if (unshadedClass === 'bg-success') hasGreen = true;
      }
    });

    // Priority: red > orange > green
    if (hasRed) return 'bg-danger';
    if (hasOrange) return 'bg-warning';
    if (hasGreen) return 'bg-success';
    
    // No data available
    return 'bg-secondary';
  }

  loadAllActiveBurnsFuelMoisture(): void {
    const activeBurns = this.getActiveBurns();
    if (activeBurns.length === 0) {
      console.log('No active burns to load fuel moisture for');
      return;
    }

    // Collect all burn coups from active burns
    const allBurnCoups: any[] = [];
    activeBurns.forEach(burn => {
      if (burn.burnCoups && burn.burnCoups.length > 0) {
        allBurnCoups.push(...burn.burnCoups);
      }
    });

    if (allBurnCoups.length === 0) {
      console.log('No burn coups in active burns');
      return;
    }

    this.siteTwinsLoading = true;
    // Don't clear existing data - just add to it
    // this.burnCoupSiteTwins.clear();

    const siteTypeCode = 'Fuel Moisture';
    let completedRequests = 0;
    const totalRequests = allBurnCoups.length;

    console.log(`Loading site twins for ${totalRequests} burn coups from active burns...`);

    allBurnCoups.forEach(burnCoup => {
      // Skip if we already have data for this burn coup
      if (this.burnCoupSiteTwins.has(burnCoup.id)) {
        completedRequests++;
        if (completedRequests === totalRequests) {
          this.siteTwinsLoading = false;
        }
        return;
      }

      if (!burnCoup.geoAsset || !burnCoup.geoAsset.identifier) {
        console.warn('Burn coup missing geoAsset or identifier:', burnCoup);
        completedRequests++;
        if (completedRequests === totalRequests) {
          this.siteTwinsLoading = false;
        }
        return;
      }

      const siteTwinUri = burnCoup.geoAsset.identifier;

      this.siteTwinService.SiteTwinGetSiteTwinByUri({
        customerId: this.app.customerId,
        siteTypeCode: siteTypeCode,
        siteTwinUri: siteTwinUri.toLowerCase()
      }).subscribe(
        (siteTwin) => {
          this.burnCoupSiteTwins.set(burnCoup.id, siteTwin);
          console.log(`Loaded site twin for burn coup ${burnCoup.id} (${siteTwinUri}):`, siteTwin);
          completedRequests++;
          if (completedRequests === totalRequests) {
            this.siteTwinsLoading = false;
            console.log('All site twins loaded for active burns. Total:', this.burnCoupSiteTwins.size);
            // Detect fuel moisture changes and trigger animations
            this.detectFuelMoistureChanges();
          }
        },
        (error) => {
          console.error(`Failed to load site twin for burn coup ${burnCoup.id} (${siteTwinUri}):`, error);
          completedRequests++;
          if (completedRequests === totalRequests) {
            this.siteTwinsLoading = false;
            // Detect changes even if some requests failed
            this.detectFuelMoistureChanges();
          }
        }
      );
    });
  }

  getFuelMoistureValue(burnCoupId: number, shade?: boolean): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }
    // Look for Fuel Moisture in the parameters keys
    const keys = Object.keys(siteTwin.parameters);
    const fuelMoistureKeys = keys.filter(k => 
      k.toLowerCase().includes('fuel') && k.toLowerCase().includes('moisture')
    );

    if (fuelMoistureKeys.length === 0) {
      return null;
    }

    // If shade parameter is not specified, return the first value found
    if (shade === undefined) {
      const parameter = siteTwin.parameters[fuelMoistureKeys[0]];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        return null;
      }
      return parameter.latestValue.value;
    }

    // Filter by shade metatag
    for (const key of fuelMoistureKeys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        continue;
      }

      // Check if metaData has shade tag matching the requested value
      const p = parameter as any;
      if (p.metaData && p.metaData['shade'] && p.metaData["unitType"] == "Fuel Moisture") {
        const isShaded = p.metaData['shade'].toLowerCase() === 'true';
        if (isShaded === shade) {
          return p.latestValue.value;
        }
      }
    }

    return null;
  }

  getFuelMoistureShadedValue(burnCoupId: number): string | null {
    return this.getFuelMoistureValue(burnCoupId, true);
  }

  getFuelMoistureUnshadedValue(burnCoupId: number): string | null {
    return this.getFuelMoistureValue(burnCoupId, false);
  }

  /**
   * Check if fuel moisture data is older than 6 hours
   */
  isFuelMoistureDataStale(burnCoupId: number, shade: boolean): boolean {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return false;
    }
    
    const keys = Object.keys(siteTwin.parameters);
    const fuelMoistureKeys = keys.filter(k => 
      k.toLowerCase().includes('fuel') && k.toLowerCase().includes('moisture')
    );

    for (const key of fuelMoistureKeys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData['shade'] && p.metaData["unitType"] == "Fuel Moisture") {
        const isShaded = p.metaData['shade'].toLowerCase() === 'true';
        if (isShaded === shade && p.latestValue.timeStamp) {
          const dataTime = new Date(p.latestValue.timeStamp);
          const now = new Date();
          const sixHoursInMs = 6 * 60 * 60 * 1000; // 6 hours in milliseconds
          const timeDiff = now.getTime() - dataTime.getTime();
          return timeDiff > sixHoursInMs;
        }
      }
    }

    return false;
  }

  /**
   * Get badge class for fuel moisture value, returns bg-light if data is stale (>6 hours old)
   */
  getFuelMoistureBadgeClassWithStaleCheck(burnCoupId: number, value: string | null, shade: boolean = false): string {
    if (!value) return 'bg-secondary';
    
    // Check if data is stale (older than 6 hours)
    if (this.isFuelMoistureDataStale(burnCoupId, shade)) {
      return 'bg-light';
    }
    
    return this.getFuelMoistureBadgeClass(value, shade);
  }

  getFuelMoistureBadgeClass(value: string | null, shade: boolean = false): string {
    if (!value) return 'bg-secondary';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'bg-secondary';

    // Color coding based on fuel moisture percentage
    if (shade) {
      // Shaded fuel moisture rules
      if (numValue < 17) return 'bg-danger';
      return 'bg-success';
    } else {
      // Unshaded fuel moisture rules
      if (numValue < 9) return 'bg-danger';
      if (numValue < 14) return 'bg-success';
      if (numValue < 16) return 'bg-warning';
      return 'bg-danger';
    }
  }

  /**
   * Get fuel moisture timestamp for a burn coup
   */
  getFuelMoistureTimestamp(burnCoupId: number, shade?: boolean): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }
    
    const keys = Object.keys(siteTwin.parameters);
    const fuelMoistureKeys = keys.filter(k => 
      k.toLowerCase().includes('fuel') && k.toLowerCase().includes('moisture')
    );

    if (fuelMoistureKeys.length === 0) {
      return null;
    }

    // If shade parameter is not specified, return the first timestamp found
    if (shade === undefined) {
      const parameter = siteTwin.parameters[fuelMoistureKeys[0]];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.timeStamp) {
        return null;
      }
      return parameter.latestValue.timeStamp;
    }

    // Filter by shade metatag
    for (const key of fuelMoistureKeys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.timeStamp) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData['shade'] && p.metaData["unitType"] == "Fuel Moisture") {
        const isShaded = p.metaData['shade'].toLowerCase() === 'true';
        if (isShaded === shade) {
          return p.latestValue.timeStamp;
        }
      }
    }

    return null;
  }

  /**
   * Get the age of fuel moisture data in milliseconds
   */
  getFuelMoistureAge(burnCoupId: number, shade?: boolean): number | null {
    let timestamp: string | null = null;
    
    if (shade === undefined) {
      // Get timestamp from either shaded or unshaded (preferring shaded if available)
      timestamp = this.getFuelMoistureTimestamp(burnCoupId, true);
      if (!timestamp) {
        timestamp = this.getFuelMoistureTimestamp(burnCoupId, false);
      }
    } else {
      // Get timestamp for specific shade value
      timestamp = this.getFuelMoistureTimestamp(burnCoupId, shade);
    }
    
    if (!timestamp) {
      return null;
    }

    const now = new Date();
    const time = new Date(timestamp);
    return now.getTime() - time.getTime();
  }

  /**
   * Get the age of shaded fuel moisture data in milliseconds
   */
  getFuelMoistureShadedAge(burnCoupId: number): number | null {
    return this.getFuelMoistureAge(burnCoupId, true);
  }

  /**
   * Get the age of unshaded fuel moisture data in milliseconds
   */
  getFuelMoistureUnshadedAge(burnCoupId: number): number | null {
    return this.getFuelMoistureAge(burnCoupId, false);
  }

  /**
   * Format age in milliseconds to human-readable string
   */
  formatAge(ageMs: number | null): string {
    if (ageMs === null || ageMs === undefined) {
      return 'N/A';
    }

    const minutes = Math.floor(ageMs / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}d ${hours % 24}h`;
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else {
      return `${minutes}m`;
    }
  }

  /**
   * Get SDI (Soil Dryness Index) value for a burn coup
   */
  getSDIValue(burnCoupId: number): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }

    const keys = Object.keys(siteTwin.parameters);
    
    for (const key of keys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData["unitType"] === "SDI") {
        return Math.round(p.latestValue.value).toString();
      }
    }

    return null;
  }

  /**
   * Get SDI timestamp for a burn coup
   */
  getSDITimestamp(burnCoupId: number): string | null {
    const siteTwin = this.burnCoupSiteTwins.get(burnCoupId);
    if (!siteTwin || !siteTwin.parameters) {
      return null;
    }

    const keys = Object.keys(siteTwin.parameters);
    
    for (const key of keys) {
      const parameter = siteTwin.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.timeStamp) {
        continue;
      }

      const p = parameter as any;
      if (p.metaData && p.metaData["unitType"] === "Soil Dryness Index") {
        return p.latestValue.timeStamp;
      }
    }

    return null;
  }

  /**
   * Get the age of SDI data in milliseconds
   */
  getSDIAge(burnCoupId: number): number | null {
    const timestamp = this.getSDITimestamp(burnCoupId);
    
    if (!timestamp) {
      return null;
    }

    const now = new Date();
    const time = new Date(timestamp);
    return now.getTime() - time.getTime();
  }

  /**
   * Check if SDI data is older than 40 hours
   */
  isSDIDataStale(burnCoupId: number): boolean {
    const timestamp = this.getSDITimestamp(burnCoupId);
    if (!timestamp) {
      return false;
    }
    
    const dataTime = new Date(timestamp);
    const now = new Date();
    const fortyHoursInMs = 40 * 60 * 60 * 1000; // 40 hours in milliseconds
    const timeDiff = now.getTime() - dataTime.getTime();
    return timeDiff > fortyHoursInMs;
  }

  /**
   * Get badge class for SDI value
   */
  getSDIBadgeClass(value: string | null): string {
    if (!value) return 'bg-secondary';
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return 'bg-secondary';

    // Color coding based on SDI value
    // Higher SDI = higher fire risk
    if (numValue > 100) return 'bg-danger';      // Over 100 - dangerous
    return 'bg-success';                          // Under/equal 100 - safe
  }

  /**
   * Get badge class for SDI value, returns bg-light if data is stale (>6 hours old)
   */
  getSDIBadgeClassWithStaleCheck(burnCoupId: number, value: string | null): string {
    if (!value) return 'bg-secondary';
    
    // Check if data is stale (older than 6 hours)
    if (this.isSDIDataStale(burnCoupId)) {
      return 'bg-light';
    }
    
    return this.getSDIBadgeClass(value);
  }

  /**
   * Get fuel moisture value from a site object (for map markers)
   */
  getSiteFuelMoistureValue(site: any, shade?: boolean): string | null {
    if (!site || !site.parameters) {
      return null;
    }
    
    // Look for Fuel Moisture in the parameters keys
    const keys = Object.keys(site.parameters);
    const fuelMoistureKeys = keys.filter(k => 
      k.toLowerCase().includes('fuel') && k.toLowerCase().includes('moisture')
    );

    if (fuelMoistureKeys.length === 0) {
      return null;
    }

    // If shade parameter is not specified, return the first value found
    if (shade === undefined) {
      const parameter = site.parameters[fuelMoistureKeys[0]];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        return null;
      }
      return parameter.latestValue.value;
    }

    // Filter by shade metatag
    for (const key of fuelMoistureKeys) {
      const parameter = site.parameters[key];
      if (!parameter || !parameter.latestValue || !parameter.latestValue.value) {
        continue;
      }

      // Check if metaData has shade tag matching the requested value
      const p = parameter as any;
      if (p.metaData && p.metaData['shade'] && p.metaData["unitType"] == "Fuel Moisture") {
        const isShaded = p.metaData['shade'].toLowerCase() === 'true';
        if (isShaded === shade) {
          return p.latestValue.value;
        }
      }
    }

    return null;
  }

  /**
   * Convert Bootstrap badge class to actual color for inline styles
   */
  getBadgeColorFromClass(badgeClass: string): string {
    switch (badgeClass) {
      case 'bg-danger':
        return '#dc3545';
      case 'bg-success':
        return '#28a745';
      case 'bg-warning':
        return '#ffc107';
      case 'bg-secondary':
        return '#6c757d';
      default:
        return '#6c757d';
    }
  }

  getCoupColorByFuelMoisture(burnCoupId: number): string | null {
    const shadedValue = this.getFuelMoistureShadedValue(burnCoupId);
    const unshadedValue = this.getFuelMoistureUnshadedValue(burnCoupId);

    console.log(`getCoupColorByFuelMoisture for burnCoup ${burnCoupId}: shaded=${shadedValue}, unshaded=${unshadedValue}`);

    // If neither value exists, return null to use default colors
    if (shadedValue === null && unshadedValue === null) {
      return null;
    }

    // Check if data is stale (skip stale data for color determination)
    const shadedIsStale = shadedValue !== null && this.isFuelMoistureDataStale(burnCoupId, true);
    const unshadedIsStale = unshadedValue !== null && this.isFuelMoistureDataStale(burnCoupId, false);

    // If both are stale or don't exist, return null
    if ((shadedValue === null || shadedIsStale) && (unshadedValue === null || unshadedIsStale)) {
      return null;
    }

    // Get the badge classes for each (only for non-stale data)
    const shadedClass = (shadedValue !== null && !shadedIsStale) ? this.getFuelMoistureBadgeClass(shadedValue, true) : null;
    const unshadedClass = (unshadedValue !== null && !unshadedIsStale) ? this.getFuelMoistureBadgeClass(unshadedValue, false) : null;

    // Priority: red > orange > green
    // If any fuel moisture is red, return red
    if (shadedClass === 'bg-danger' || unshadedClass === 'bg-danger') {
      return '#dc3545'; // Bootstrap danger red
    }

    // If any fuel moisture is orange (warning), return orange
    if (shadedClass === 'bg-warning' || unshadedClass === 'bg-warning') {
      return '#ffc107'; // Bootstrap warning orange
    }

    // If both are green (success), return green
    if (shadedClass === 'bg-success' && unshadedClass === 'bg-success') {
      return '#28a745'; // Bootstrap success green
    }

    // If one is green and the other is null
    if (shadedClass === 'bg-success' || unshadedClass === 'bg-success') {
      return '#28a745'; // Bootstrap success green
    }

    // Default to null if we can't determine
    return null;
  }

  startFuelMoistureRefreshTimer(): void {
    console.log('Starting fuel moisture refresh timer (2 minutes)');
    this.fuelMoistureRefreshInterval = setInterval(() => {
      this.refreshFuelMoistureData();
    }, this.FUEL_MOISTURE_REFRESH_MS);
  }

  refreshFuelMoistureData(): void {
    console.log('Refreshing fuel moisture data...');
    
    // Store previous values before refresh
    this.storePreviousFuelMoistureValues();
    
    // Refresh data based on current view
    if (this.selectedBurn) {
      this.loadSiteTwinsForBurnCoups();
    } else {
      this.loadAllActiveBurnsFuelMoisture();
    }
  }

  storePreviousFuelMoistureValues(): void {
    // Store current values as previous values
    this.previousFuelMoistureValues.clear();
    
    // Get all burn coups that might have fuel moisture data
    const burnCoups: any[] = [];
    if (this.selectedBurn && this.selectedBurn.burnCoups) {
      burnCoups.push(...this.selectedBurn.burnCoups);
    } else {
      const activeBurns = this.getActiveBurns();
      activeBurns.forEach(burn => {
        if (burn.burnCoups) {
          burnCoups.push(...burn.burnCoups);
        }
      });
    }

    burnCoups.forEach(coup => {
      const key = `${coup.id}`;
      const shadedValue = this.getFuelMoistureShadedValue(coup.id);
      const unshadedValue = this.getFuelMoistureUnshadedValue(coup.id);
      this.previousFuelMoistureValues.set(key, { shaded: shadedValue, unshaded: unshadedValue });
    });
  }

  detectFuelMoistureChanges(): void {
    this.updatedFuelMoistureValues.clear();
    
    // Check for changes
    this.previousFuelMoistureValues.forEach((prevValues, coupId) => {
      const burnCoupId = parseInt(coupId);
      const currentShaded = this.getFuelMoistureShadedValue(burnCoupId);
      const currentUnshaded = this.getFuelMoistureUnshadedValue(burnCoupId);
      
      const shadedChanged = prevValues.shaded !== currentShaded;
      const unshadedChanged = prevValues.unshaded !== currentUnshaded;
      
      if (shadedChanged || unshadedChanged) {
        this.updatedFuelMoistureValues.set(coupId, {
          shaded: shadedChanged,
          unshaded: unshadedChanged
        });
        
        // Clear the animation flag after animation completes (0.8s animation + buffer)
        setTimeout(() => {
          this.updatedFuelMoistureValues.delete(coupId);
        }, 1000);
      }
    });
    
    if (this.updatedFuelMoistureValues.size > 0) {
      console.log('Detected fuel moisture changes:', this.updatedFuelMoistureValues);
    }
  }

  hasFuelMoistureUpdated(burnCoupId: number, type: 'shaded' | 'unshaded'): boolean {
    const key = `${burnCoupId}`;
    const update = this.updatedFuelMoistureValues.get(key);
    if (!update) return false;
    return type === 'shaded' ? update.shaded : update.unshaded;
  }

  private wktToGeoJSON(wkt: string): L.Layer | null {
    if (!wkt) return null;

    //console.log('Parsing WKT:', wkt);

    // Parser for MULTIPOLYGON
    // Format: MULTIPOLYGON (((lng lat, lng lat, ...)), ((lng lat, lng lat, ...)))
    if (wkt.startsWith('MULTIPOLYGON')) {
      //console.log('Parsing MULTIPOLYGON');
      
      // Extract all polygon rings - each is wrapped in ((...))
      // Use non-greedy match to capture content between (( and ))
      const polygonRegex = /\(\((.+?)\)\)/g;
      const polygons: [number, number][][] = [];
      let match;
      
      while ((match = polygonRegex.exec(wkt)) !== null) {
        let coordString = match[1];
        // Clean any stray parentheses from the start/end
        coordString = coordString.replace(/^\(+|\)+$/g, '');
        //console.log('Found polygon coordinates:', coordString);
        const coords = coordString.split(',').map(pair => {
          const [lng, lat] = pair.trim().split(/\s+/).map(Number);
          return [lat, lng] as [number, number];
        }).filter(coord => {
          const [lat, lng] = coord;
          if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            console.warn('Filtering out invalid coordinate pair:', coord);
            return false;
          }
          return true;
        });
        if (coords.length > 0) {
          polygons.push(coords);
        }
      }
      
      //console.log('Parsed', polygons.length, 'polygon(s)');
      
      if (polygons.length === 1) {
        return L.polygon(polygons[0], {
          color: '#3388ff',
          fillColor: '#3388ff',
          fillOpacity: 0.2
        });
      } else if (polygons.length > 1) {
        // Create a layer group with multiple polygons
        const layerGroup = L.featureGroup();
        polygons.forEach(coords => {
          const polygon = L.polygon(coords, {
            color: '#3388ff',
            fillColor: '#3388ff',
            fillOpacity: 0.2
          });
          layerGroup.addLayer(polygon);
        });
        return layerGroup;
      }
    }

    // Simple WKT parser for POLYGON
    // Format: POLYGON((lng lat, lng lat, ...))
    const polygonMatch = wkt.match(/POLYGON\s*\(\(([^)]+)\)\)/);
    if (polygonMatch) {
      console.log('Matched POLYGON pattern');
      const coordString = polygonMatch[1];
      console.log('Coordinate string:', coordString);
      
      const coords = coordString.split(',').map(pair => {
        const [lng, lat] = pair.trim().split(/\s+/).map(Number);
        return [lat, lng] as [number, number];
      }).filter(coord => {
        const [lat, lng] = coord;
        if (isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
          console.warn('Filtering out invalid coordinate:', coord);
          return false;
        }
        return true;
      });
      
      console.log('Parsed coordinates:', coords);
      
      return L.polygon(coords, {
        color: '#3388ff',
        fillColor: '#3388ff',
        fillOpacity: 0.2
      });
    }

    console.warn('No WKT pattern matched');
    return null;
  }

  wktToGeoJSON2(wkt: string): any {
    // Simple WKT to GeoJSON converter for POLYGON and MULTIPOLYGON
    wkt = wkt.trim();
    
    if (wkt.startsWith('POLYGON')) {
      const coordsStr = wkt.match(/\(\((.+?)\)\)/)?.[1];
      if (!coordsStr) return null;
      
      const coords = coordsStr.split(',').map(pair => {
        const [lon, lat] = pair.trim().split(' ').map(Number);
        return [lon, lat];
      }).filter(coord => {
        const [lon, lat] = coord;
        if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
          console.warn('Filtering out invalid coordinate:', coord);
          return false;
        }
        return true;
      });
      
      return {
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [coords]
        },
        properties: {}
      };
    } else if (wkt.startsWith('MULTIPOLYGON')) {
      // Simplified multipolygon parsing
      const polygons: any[] = [];
      const matches = wkt.matchAll(/\(\((.+?)\)\)/g);
      
      for (const match of matches) {
        const coordsStr = match[1];
        const coords = coordsStr.split(',').map(pair => {
          const [lon, lat] = pair.trim().split(' ').map(Number);
          return [lon, lat];
        }).filter(coord => {
          const [lon, lat] = coord;
          if (isNaN(lat) || isNaN(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
            console.warn('Filtering out invalid coordinate:', coord);
            return false;
          }
          return true;
        });
        if (coords.length > 0) {
          polygons.push([coords]);
        }
      }
      
      return {
        type: 'Feature',
        geometry: {
          type: 'MultiPolygon',
          coordinates: polygons
        },
        properties: {}
      };
    }
    
    return null;
  }

  loadPlannedBurns(): void {
    this.loading = true;
    
    this.burnListDataService.getBurnLists(this.app.customerId).subscribe(
      (data) => {
        this.plannedBurns = data;
        this.loading = false;
        // Load fuel moisture for all active burns when no burn is selected
        if (!this.selectedBurn) {
          this.loadAllActiveBurnsFuelMoisture();
          // Display all burn lists on the map
          setTimeout(() => this.displayBurnListsOnMap(), 300);
        }
      },
      (error) => {
        console.error('Failed to load planned burns', error);
        this.toasterService.error('Failed to load planned burns');
        this.loading = false;
      }
    );
  }

  selectBurn(burn: BurnList): void {
    this.selectedBurnId = burn.id;
    this.loading = true;
    this.mapRetryCount = 0; // Reset retry counter
    this.isEditMode = false; // Reset edit mode when selecting a different burn
    this.activeTab = 'details'; // Reset to details tab
    
    // Update URL with burnListId
    this.router.navigate(['/fire-foresight/burn-list', burn.id], { replaceUrl: true });
    
    this.burnListService.BurnListGetBurnList({
      customerId: this.app.customerId,
      burnListId: burn.id
    }).subscribe(
      (data) => {
        this.selectedBurn = data;
        this.loading = false;
        // Load site twins for the burn coups
        this.loadSiteTwinsForBurnCoups();
        // Load coups for the current map view when burn plan is selected (map becomes visible)
        this.loadBurnCoupsAfterMapReady();
        // Refresh fuel moisture sensor markers to update button visibility
        this.addFuelMoistureSensorMarkers();
        // Also display coups nested in the burn plan
        if (this.selectedBurn && this.selectedBurn.burnCoups && this.selectedBurn.burnCoups.length > 0) {
          this.allCoups = this.selectedBurn.burnCoups.map(bc => ({
            ...bc.geoAsset,
            //  id: bc.id,
            geometry: bc.geoAsset.geometryGeoJson
          }));
          this.displayCoupsOnMap();
        }
      },
      (error) => {
        console.error('Failed to load burn plan details', error);
        this.toasterService.error('Failed to load burn plan details');
        this.loading = false;
        // Fallback to the list item data
        this.selectedBurn = burn;
        // Load site twins for the burn coups
        this.loadSiteTwinsForBurnCoups();
        this.loadBurnCoupsAfterMapReady();
        // Refresh fuel moisture sensor markers to update button visibility
        this.addFuelMoistureSensorMarkers();
        if (this.selectedBurn && this.selectedBurn.burnCoups && this.selectedBurn.burnCoups.length > 0) {
          this.allCoups = this.selectedBurn.burnCoups.map(bc => ({
            ...bc.geoAsset,
            id: bc.id,
            geometry: bc.geoAsset.geometryGeoJson
          }));
          this.displayCoupsOnMap();
        }
      }
    );
  }

  loadSpecificBurnList(burnListId: number): void {
    this.loading = true;
    this.burnListService.BurnListGetBurnList({
      customerId: this.app.customerId,
      burnListId: burnListId
    }).subscribe(
      (data) => {
        this.selectedBurn = data;
        this.selectedBurnId = data.id;
        this.loading = false;
        this.mapRetryCount = 0;
        this.isEditMode = false;
        this.activeTab = 'details';
        
        // Load site twins for the burn coups
        this.loadSiteTwinsForBurnCoups();
        // Load coups for the current map view when burn plan is selected (map becomes visible)
        this.loadBurnCoupsAfterMapReady();
        // Refresh fuel moisture sensor markers to update button visibility
        this.addFuelMoistureSensorMarkers();
        // Also display coups nested in the burn plan
        if (this.selectedBurn && this.selectedBurn.burnCoups && this.selectedBurn.burnCoups.length > 0) {
          this.allCoups = this.selectedBurn.burnCoups.map(bc => ({
            ...bc.geoAsset,
            geometry: bc.geoAsset.geometryGeoJson
          }));
          this.displayCoupsOnMap();
        }
      },
      (error) => {
        console.error('Failed to load burn list details', error);
        this.toasterService.error('Failed to load burn list details');
        this.loading = false;
        this.selectedBurnId = null;
        // Navigate back to list without ID
        this.router.navigate(['/fire-foresight/burn-list'], { replaceUrl: true });
      }
    );
  }

  setActiveTab(tab: 'details' | 'journal'): void {
    this.activeTab = tab;
  }

  addComment(): void {
    if (!this.selectedBurn || !this.newComment.trim()) return;
    
    this.addingComment = true;
    const comment = {
      burnListId: this.selectedBurn.id,
      comment: this.newComment.trim(),
      userId: this.security.userProfile?.userId?.toString() || '',
      commentTimestamp: new Date().toISOString(),
      id: 0
    };

    this.burnListService.BurnListAddComment({
      customerId: this.app.customerId,
      burnListId: this.selectedBurn.id,
      request: {
        comment: comment.comment,
      }
    }).subscribe(
      (result) => {
        // Add the new comment to the list
        if (!this.selectedBurn.burnListComments) {
          this.selectedBurn.burnListComments = [];
        }
        this.selectedBurn.burnListComments.push(result);
        this.newComment = '';
        this.addingComment = false;
      },
      (error) => {
        console.error('Failed to add comment', error);
        this.toasterService.error('Failed to add comment');
        this.addingComment = false;
      }
    );
  }

  onCommentKeyDown(event: KeyboardEvent): void {
    // Check if Ctrl+Enter (or Cmd+Enter on Mac) is pressed
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault(); // Prevent newline from being added
      this.addComment();
    }
  }

  isCurrentUserComment(comment: any): boolean {
    return comment.userId === this.security.userProfile?.userId;  
  }

  getSortedBurnCoups(): any[] {
    if (!this.selectedBurn || !this.selectedBurn.burnCoups) {
      return [];
    }
    
    // Create a copy to avoid mutating the original array
    const coups = [...this.selectedBurn.burnCoups];
    
    // Separate enabled and disabled coups
    const enabledCoups = coups.filter(c => !c.disabled);
    const disabledCoups = coups.filter(c => c.disabled);
    
    // Sort each group alphabetically by identifier
    enabledCoups.sort((a, b) => {
      const idA = a.geoAsset?.identifier || '';
      const idB = b.geoAsset?.identifier || '';
      return idA.localeCompare(idB);
    });
    
    disabledCoups.sort((a, b) => {
      const idA = a.geoAsset?.identifier || '';
      const idB = b.geoAsset?.identifier || '';
      return idA.localeCompare(idB);
    });
    
    // Return enabled coups first, then disabled coups
    return [...enabledCoups, ...disabledCoups];
  }

  enableEditMode(): void {
    if (!this.selectedBurn) return;
    this.isEditMode = true;
    // Create a copy of current values for editing
    this.editingBurn = {
      id: this.selectedBurn.id,
      planName: this.selectedBurn.planName,
      expectedStartTime: this.formatDateForInput(this.selectedBurn.expectedStartTime) as any,
      expectedEndTime: this.formatDateForInput(this.selectedBurn.expectedEndTime) as any,
      burnListStatusId: this.selectedBurn.burnListStatusId,
      burnListRegionId: this.selectedBurn.burnListRegionId
    };
  }

  formatDateForInput(date: any): string | null {
    if (!date) return null;
    const d = new Date(date);
    if (isNaN(d.getTime())) return null;
    // Format as YYYY-MM-DDThh:mm for datetime-local input
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  cancelEdit(): void {
    this.isEditMode = false;
    this.editingBurn = {};
  }

  saveEdit(): void {
    if (!this.selectedBurn || !this.editingBurn.id) return;
    
    this.loading = true;
    // Convert datetime-local strings back to ISO strings
    const startTime = this.editingBurn.expectedStartTime ? new Date(this.editingBurn.expectedStartTime as any).toISOString() : null;
    const endTime = this.editingBurn.expectedEndTime ? new Date(this.editingBurn.expectedEndTime as any).toISOString() : null;
    
    const updatePayload = {
      ...this.selectedBurn,
      id: this.editingBurn.id, // Explicitly include the ID
      customerId: this.selectedBurn.customerId, // Explicitly include customerId
      planName: this.editingBurn.planName,
      expectedStartTime: startTime,
      expectedEndTime: endTime,
      burnListStatusId: this.editingBurn.burnListStatusId,
      burnListRegionId: this.editingBurn.burnListRegionId,
    };

    console.log('Updating burn list with payload:', updatePayload);
    
    this.burnListService.BurnListUpdateBurnList(updatePayload).subscribe(
      (result) => {
        this.toasterService.success('Burn plan updated successfully');
        // Clear cache to ensure fresh data on next load
        this.burnListDataService.clearBurnListsCache(this.app.customerId);
        // Update the local copy with new values
        this.selectedBurn.planName = this.editingBurn.planName;
        this.selectedBurn.expectedStartTime = startTime;
        this.selectedBurn.expectedEndTime = endTime;
        this.selectedBurn.burnListStatusId = this.editingBurn.burnListStatusId;
        this.selectedBurn.burnListRegionId = this.editingBurn.burnListRegionId;
        
        // Update the burn in the plannedBurns array
        const index = this.plannedBurns.findIndex(b => b.id === this.selectedBurn!.id);
        if (index !== -1) {
          this.plannedBurns[index] = { ...this.plannedBurns[index], ...updatePayload };
        }
        
        this.isEditMode = false;
        this.editingBurn = {};
        this.loading = false;
      },
      (error) => {
        console.error('Failed to update burn plan', error);
        this.toasterService.error('Failed to update burn plan');
        this.loading = false;
      }
    );
  }

  loadSiteTwinsForBurnCoups(): void {
    if (!this.selectedBurn || !this.selectedBurn.burnCoups || this.selectedBurn.burnCoups.length === 0) {
      console.log('No burn coups to load site twins for');
      return;
    }

    this.siteTwinsLoading = true;
    this.burnCoupSiteTwins.clear();

    const siteTypeCode = 'Fuel Moisture';
    let completedRequests = 0;
    const totalRequests = this.selectedBurn.burnCoups.length;

    console.log(`Loading site twins for ${totalRequests} burn coups...`);

    this.selectedBurn.burnCoups.forEach(burnCoup => {
      if (!burnCoup.geoAsset || !burnCoup.geoAsset.identifier.toLocaleLowerCase()) {
        console.warn('Burn coup missing geoAsset or identifier:', burnCoup);
        completedRequests++;
        if (completedRequests === totalRequests) {
          this.siteTwinsLoading = false;
        }
        return;
      }

      const siteTwinUri = burnCoup.geoAsset.identifier;

      this.siteTwinService.SiteTwinGetSiteTwinByUri({
        customerId: this.app.customerId,
        siteTypeCode: siteTypeCode,
        siteTwinUri: siteTwinUri.toLowerCase()
      }).subscribe(
        (siteTwin) => {
          this.burnCoupSiteTwins.set(burnCoup.id, siteTwin);
          console.log(`Loaded site twin for burn coup ${burnCoup.id} (${siteTwinUri}):`, siteTwin);
          completedRequests++;
          if (completedRequests === totalRequests) {
            this.siteTwinsLoading = false;
            console.log('All site twins loaded. Total:', this.burnCoupSiteTwins.size);
            console.log('Refreshing map to show fuel moisture colors...');
            // Detect fuel moisture changes and trigger animations
            this.detectFuelMoistureChanges();
            // Refresh map to show fuel moisture colors
            this.displayCoupsOnMap();
          }
        },
        (error) => {
          console.error(`Failed to load site twin for burn coup ${burnCoup.id} (${siteTwinUri}):`, error);
          completedRequests++;
          if (completedRequests === totalRequests) {
            this.siteTwinsLoading = false;
            // Detect changes even if some requests failed
            this.detectFuelMoistureChanges();
            // Refresh map even if some requests failed
            this.displayCoupsOnMap();
          }
        }
      );
    });
  }

  loadBurnCoupsAfterMapReady(): void {
    // Wait for map to be ready, then load coups
    if (!this.map || !this.map.map) {
      if (this.mapRetryCount < this.maxMapRetries) {
        this.mapRetryCount++;
        console.log(`Waiting for map to be ready... (attempt ${this.mapRetryCount}/${this.maxMapRetries})`);
        setTimeout(() => this.loadBurnCoupsAfterMapReady(), 300);
      } else {
        console.error('Map failed to initialize after', this.maxMapRetries, 'attempts');
        //this.toasterService.error('Failed to initialize map');
      }
      return;
    }

    this.mapRetryCount = 0;
    
    // No longer setting up map event listeners for auto-load
    
    // Enable fitBounds for initial load only
    this.shouldFitBounds = true;
    
    // Now load coups for current view
    this.loadBurnCoupsForCurrentView();
  }

  deselectBurn(): void {
    this.selectedBurn = null;
    this.selectedBurnId = null;
    this.clearCoupsFromMap();
    // Clear the burnListId from the URL
    this.router.navigate(['/fire-foresight/burn-list'], { replaceUrl: true });
    // Display burn list overview map
    setTimeout(() => this.displayBurnListsOnMap(), 300);
  }

  openCreateBurnModal(): void {
    this.resetNewBurnForm();
    this.createBurnModal.show();
  }

  resetNewBurnForm(): void {
    this.newBurn = {
      planName: '',
      expectedStartTime: null,
      expectedEndTime: null
    };
  }

  createNewBurn(): void {
    if (!this.newBurn.planName || !this.newBurn.expectedStartTime) {
      this.toasterService.warning('Please provide a plan name and expected start time');
      return;
    }

    if (!this.newBurn.burnListRegionId || !this.newBurn.burnListTypeId) {
      this.toasterService.warning('Please select a region and burn type');
      return;
    }

    this.savingBurn = true;

    const burnList: BurnList = {
      id: 0,
      planName: this.newBurn.planName,
      expectedStartTime: this.newBurn.expectedStartTime,
      expectedEndTime: this.newBurn.expectedEndTime,
      customerId: this.app.customerId,
      createdDate: new Date().toISOString(),
      burnListRegionId: this.newBurn.burnListRegionId,
      burnListTypeId: this.newBurn.burnListTypeId,
      burnCoups: []
    };

    this.burnListService.BurnListCreateBurnList(burnList).subscribe(
      (data) => {
        this.toasterService.success('Planned burn created successfully');
        this.createBurnModal.hide();
        this.savingBurn = false;
        // Clear cache to ensure fresh data on next load
        this.burnListDataService.clearBurnListsCache(this.app.customerId);
        this.loadPlannedBurns(); // Reload the list
        // Optionally select the newly created burn
        if (data && data.id) {
          this.selectBurn(data);
        }
        // Reset form
        this.newBurn = {
          planName: '',
          expectedStartTime: null,
          expectedEndTime: null,
          burnListRegionId: null,
          burnListTypeId: null
        };
      },
      (error) => {
        console.error('Failed to create planned burn', error);
        this.toasterService.error('Failed to create planned burn');
        this.savingBurn = false;
      }
    );
  }

  // Distance and filtering methods for EPA Air Quality sites

  /**
   * Calculate distance between two lat/lon points in kilometers using Haversine formula
   */
  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Radius of Earth in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.cos(this.toRadians(lat1)) * Math.cos(this.toRadians(lat2)) *
              Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    
    return distance;
  }

  toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Calculate centroid from GeoJSON geometry
   */
  getCentroidFromGeoJson(geoJson: string): { lat: number, lon: number } | null {
    try {
      // Parse the GeoJSON if it's a string, otherwise use as-is
      const parsed = typeof geoJson === 'string' ? JSON.parse(geoJson) : geoJson;
      
      if (!parsed) {
        console.warn('getCentroidFromGeoJson: No parsed GeoJSON');
        return null;
      }

      // Extract the actual geometry object
      // Could be a Feature (with geometry property) or raw Geometry (with type and coordinates)
      let geometry: any;
      if (parsed.type === 'Feature' && parsed.geometry) {
        geometry = parsed.geometry;
      } else if (parsed.type === 'FeatureCollection' && parsed.features && parsed.features.length > 0) {
        // For FeatureCollection, use the first feature's geometry
        geometry = parsed.features[0].geometry;
      } else if (parsed.coordinates) {
        // Raw geometry object
        geometry = parsed;
      } else {
        console.warn('getCentroidFromGeoJson: Unrecognized GeoJSON structure', parsed);
        return null;
      }

      if (!geometry || !geometry.coordinates) {
        console.warn('getCentroidFromGeoJson: No coordinates found in geometry', geometry);
        return null;
      }

      let allCoords: number[][] = [];

      // Handle different GeoJSON geometry types
      if (geometry.type === 'Polygon') {
        // Polygon coordinates are [[[lon, lat], [lon, lat], ...]]
        allCoords = geometry.coordinates[0];
      } else if (geometry.type === 'MultiPolygon') {
        // MultiPolygon coordinates are [[[[lon, lat], ...]], [[[lon, lat], ...]]]
        geometry.coordinates.forEach((polygon: any) => {
          allCoords = allCoords.concat(polygon[0]);
        });
      } else if (geometry.type === 'Point') {
        // Point is just [lon, lat]
        return { lat: geometry.coordinates[1], lon: geometry.coordinates[0] };
      } else if (geometry.type === 'LineString') {
        // LineString is [[lon, lat], [lon, lat], ...]
        allCoords = geometry.coordinates;
      } else if (geometry.type === 'MultiLineString') {
        // MultiLineString is [[[lon, lat], ...], [[lon, lat], ...]]
        geometry.coordinates.forEach((line: any) => {
          allCoords = allCoords.concat(line);
        });
      } else {
        console.warn('getCentroidFromGeoJson: Unsupported geometry type', geometry.type);
        return null;
      }

      if (allCoords.length === 0) {
        console.warn('getCentroidFromGeoJson: No coordinates extracted');
        return null;
      }

      // Calculate average lat/lon
      let sumLat = 0;
      let sumLon = 0;
      allCoords.forEach((coord: number[]) => {
        sumLon += coord[0];
        sumLat += coord[1];
      });

      return {
        lat: sumLat / allCoords.length,
        lon: sumLon / allCoords.length
      };
    } catch (error) {
      console.error('Error parsing GeoJSON:', error, geoJson);
      return null;
    }
  }

  /**
   * Calculate the centroid of all burn coups in a burn list
   */
  getBurnListCentroid(burnList: BurnList): { lat: number, lon: number } | null {
    const enabledCoups = burnList?.burnCoups?.filter(coup => !coup.disabled) || [];
    console.log('getBurnListCentroid called', {
      hasBurnList: !!burnList,
      totalCoups: burnList?.burnCoups?.length || 0,
      enabledCoups: enabledCoups.length
    });
    
    if (enabledCoups.length === 0) {
      console.warn('No enabled coups in burn list');
      return null;
    }

    const centroids: { lat: number, lon: number }[] = [];

    enabledCoups.forEach((coup, index) => {
      if (coup.geoAsset && coup.geoAsset.geometryGeoJson) {
        console.log(`Processing coup ${index + 1}/${enabledCoups.length}: ${coup.geoAsset.identifier}`);
        const centroid = this.getCentroidFromGeoJson(coup.geoAsset.geometryGeoJson);
        if (centroid) {
          console.log(`Centroid found for coup ${coup.geoAsset.identifier}:`, centroid);
          centroids.push(centroid);
        } else {
          console.warn(`Failed to get centroid for coup ${coup.geoAsset.identifier}`);
        }
      } else {
        console.warn(`Coup ${index + 1} missing geoAsset or geometryGeoJson:`, coup);
      }
    });

    if (centroids.length === 0) {
      console.error('No valid centroids found from coup geometries. Checked', enabledCoups.length, 'coups');
      return null;
    }

    // Calculate average of all coup centroids
    let sumLat = 0;
    let sumLon = 0;
    centroids.forEach(c => {
      sumLat += c.lat;
      sumLon += c.lon;
    });

    const result = {
      lat: sumLat / centroids.length,
      lon: sumLon / centroids.length
    };
    console.log(`Calculated burn list centroid from ${centroids.length} coups:`, result);
    return result;
  }

  /**
   * Get air quality sites within 20km of burn list centroid, or closest site if none within 20km
   */
  getNearbyOrClosestAirQualitySites(burnList: BurnList): Array<any & { distance: number }> {
    const centroid = this.getBurnListCentroid(burnList);
    console.log('getNearbyOrClosestAirQualitySites called', {
      hasBurnList: !!burnList,
      centroid: centroid,
      airQualitySitesCount: this.airQualitySites.length,
      airQualitySitesLoading: this.airQualitySitesLoading
    });
    
    if (!centroid) {
      console.warn('No centroid available for burn list');
      return [];
    }
    
    if (this.airQualitySites.length === 0) {
      console.warn('No air quality sites loaded yet');
      return [];
    }

    const maxDistance = 20; // kilometers
    const sitesWithDistance = this.airQualitySites
      .filter(site => site.latitude && site.longitude)
      .map(site => ({
        ...site,
        distance: this.calculateDistance(
          centroid.lat,
          centroid.lon,
          site.latitude,
          site.longitude
        )
      }))
      .sort((a, b) => a.distance - b.distance); // Sort by distance

    console.log('Sites with distances:', sitesWithDistance.map(s => ({ name: s.siteName, distance: s.distance })));

    // Get sites within 20km
    const nearbySites = sitesWithDistance.filter(site => site.distance <= maxDistance);

    // If there are sites within 20km, return them
    if (nearbySites.length > 0) {
      console.log(`Found ${nearbySites.length} sites within 20km`);
      return nearbySites;
    }

    // Otherwise, return just the closest site
    const result = sitesWithDistance.length > 0 ? [sitesWithDistance[0]] : [];
    console.log('No sites within 20km, returning closest:', result.length > 0 ? result[0].siteName : 'none');
    return result;
  }

  /**
   * Get the nearest weather station to the burn list
   */
  getNearestWeatherStation(burnList: BurnList): any & { distance: number } | null {
    const centroid = this.getBurnListCentroid(burnList);
    console.log('getNearestWeatherStation called', {
      hasBurnList: !!burnList,
      centroid: centroid,
      weatherStationSitesCount: this.weatherStationSites.length,
      weatherStationSitesLoading: this.weatherStationSitesLoading
    });
    
    if (!centroid) {
      console.warn('No centroid available for burn list');
      return null;
    }
    
    if (this.weatherStationSites.length === 0) {
      console.warn('No weather station sites loaded yet');
      return null;
    }

    const sitesWithDistance = this.weatherStationSites
      .filter(site => site.latitude && site.longitude)
      .map(site => ({
        ...site,
        distance: this.calculateDistance(
          centroid.lat,
          centroid.lon,
          site.latitude,
          site.longitude
        )
      }))
      .sort((a, b) => a.distance - b.distance); // Sort by distance

    const nearest = sitesWithDistance.length > 0 ? sitesWithDistance[0] : null;
    console.log('Nearest weather station:', nearest ? { name: nearest.siteName, distance: nearest.distance } : 'none');
    return nearest;
  }

  /**
   * Get temperature value from weather station
   */
  getTemperatureValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }
    
    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'Air Temperature') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }
    
    return null;
  }

  /**
   * Get humidity value from weather station
   */
  getHumidityValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }
    
    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && (parameter.unitType === 'Relative Humidity' || parameter.unitType === 'Humidity')) {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }
    
    return null;
  }

  /**
   * Get wind speed value from weather station
   */
  getWindSpeedValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }
    
    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'Wind Speed') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }
    
    return null;
  }

  /**
   * Get wind direction value from weather station
   */
  getWindDirectionValue(site: any): number | null {
    if (!site.parameters) {
      return null;
    }
    
    const keys = Object.keys(site.parameters);
    for (const key of keys) {
      const parameter = site.parameters[key];
      if (parameter && parameter.unitType === 'Wind Direction') {
        if (parameter.latestValue !== undefined && parameter.latestValue !== null) {
          return parseFloat(parameter.latestValue.value);
        }
      }
    }
    
    return null;
  }

  // Move Burn Coup Methods

  openMoveCoupModal(coup: any): void {
    this.coupToMove = coup;
    this.targetBurnListId = null;
    this.createNewTargetList = false;
    this.newTargetBurnList = {
      planName: '',
      expectedStartTime: null,
      burnListRegionId: this.selectedBurn.burnListRegionId,
      burnListTypeId: null,
      customerId: this.app.customerId
    };
    
    // Find eligible target burn lists (same region, type "Burn Day")
    const burnDayType = this.burnListTypes.find(t => t.name === 'Burn Day');
    
    this.availableTargetBurnLists = this.plannedBurns.filter(bl => 
      bl.id !== this.selectedBurn.id && // Not the current burn list
      bl.burnListRegionId === this.selectedBurn.burnListRegionId && // Same region
      bl.burnListTypeId === burnDayType?.id // Type is "Burn Day"
    );
    
    this.moveCoupModal.show();
  }

  selectCreateNewList(): void {
    this.createNewTargetList = true;
    this.targetBurnListId = null;
  }

  selectExistingList(): void {
    this.createNewTargetList = false;
  }

  async moveBurnCoup(): Promise<void> {
    if (!this.coupToMove) return;

    if (this.createNewTargetList) {
      // Create new burn list first
      if (!this.newTargetBurnList.planName || !this.newTargetBurnList.expectedStartTime) {
        this.toasterService.error('Please enter a name and start date for the new burn list');
        return;
      }

      // Set the type to "Burn Day"
      const burnDayType = this.burnListTypes.find(t => t.name === 'Burn Day');
      if (!burnDayType) {
        this.toasterService.error('Could not find Burn Day type');
        return;
      }
      
      this.newTargetBurnList.burnListTypeId = burnDayType.id;
      this.newTargetBurnList.burnListRegionId = this.selectedBurn.burnListRegionId;
      this.newTargetBurnList.customerId = this.app.customerId;

      this.movingCoup = true;

      try {
        // Create the new burn list
        const newBurnList = await this.createBurnListForMove();
        
        // Move the coup to the new list
        await this.performCoupMove(newBurnList.id);
        
        this.toasterService.success('Burn coup moved to new burn list successfully');
        this.moveCoupModal.hide();
        this.loadPlannedBurns();
      } catch (error) {
        console.error('Failed to move burn coup:', error);
        this.toasterService.error('Failed to move burn coup');
      } finally {
        this.movingCoup = false;
      }
    } else {
      // Move to existing burn list
      if (!this.targetBurnListId) {
        this.toasterService.error('Please select a target burn list');
        return;
      }

      this.movingCoup = true;

      try {
        await this.performCoupMove(this.targetBurnListId);
        this.toasterService.success('Burn coup moved successfully');
        this.moveCoupModal.hide();
        this.loadPlannedBurns();
      } catch (error) {
        console.error('Failed to move burn coup:', error);
        this.toasterService.error('Failed to move burn coup');
      } finally {
        this.movingCoup = false;
      }
    }
  }

  private createBurnListForMove(): Promise<BurnList> {
    return new Promise((resolve, reject) => {
      this.burnListService.BurnListCreateBurnList(
        this.newTargetBurnList as BurnList
      ).subscribe(
        (burnList) => resolve(burnList),
        (error) => reject(error)
      );
    });
  }

  private async performCoupMove(targetBurnListId: number): Promise<void> {
    // First, remove from current burn list
    await new Promise<void>((resolve, reject) => {
      this.burnListService.BurnListRemoveBurnCoup({
        customerId: this.app.customerId,
        burnListId: this.selectedBurn.id,
        geoAssetId: this.coupToMove.geoAsset.id
      }).subscribe(
        () => resolve(),
        (error) => reject(error)
      );
    });

    // Then, add to target burn list
    await new Promise<void>((resolve, reject) => {
      this.burnListService.BurnListAddBurnCoup({
        customerId: this.app.customerId,
        burnListId: targetBurnListId,
        geoAssetId: this.coupToMove.geoAsset.id
      }).subscribe(
        () => resolve(),
        (error) => reject(error)
      );
    });

    // Reload the current burn list
    await new Promise<void>((resolve, reject) => {
      this.burnListService.BurnListGetBurnList({
        customerId: this.app.customerId,
        burnListId: this.selectedBurn.id
      }).subscribe(
        (burnList) => {
          this.selectedBurn = burnList;
          resolve();
        },
        (error) => reject(error)
      );
    });
  }

  cancelMoveCoup(): void {
    this.moveCoupModal.hide();
    this.coupToMove = null;
  }

  // Air Quality methods for individual burn coups

  /**
   * Get the centroid of a specific burn coup
   */
  getBurnCoupCentroid(burnCoup: any): { lat: number, lon: number } | null {
    if (!burnCoup || !burnCoup.geoAsset || !burnCoup.geoAsset.geometryGeoJson) {
      return null;
    }
    return this.getCentroidFromGeoJson(burnCoup.geoAsset.geometryGeoJson);
  }

  /**
   * Find the closest air quality site to a specific burn coup
   */
  getClosestAirQualitySiteForBurnCoup(burnCoup: any): any & { distance: number } | null {
    const centroid = this.getBurnCoupCentroid(burnCoup);
    
    if (!centroid) {
      return null;
    }
    
    if (this.airQualitySites.length === 0) {
      return null;
    }

    const sitesWithDistance = this.airQualitySites
      .filter(site => site.latitude && site.longitude)
      .map(site => ({
        ...site,
        distance: this.calculateDistance(
          centroid.lat,
          centroid.lon,
          site.latitude,
          site.longitude
        )
      }))
      .sort((a, b) => a.distance - b.distance);

    return sitesWithDistance.length > 0 ? sitesWithDistance[0] : null;
  }

  /**
   * Get PM10 value for a specific burn coup (from closest air quality station)
   */
  getPM10ValueForBurnCoup(burnCoupId: number): number | null {
    const burnCoup = this.selectedBurn?.burnCoups?.find(c => c.id === burnCoupId);
    if (!burnCoup) {
      return null;
    }

    const closestSite = this.getClosestAirQualitySiteForBurnCoup(burnCoup);
    if (!closestSite) {
      return null;
    }

    return this.getPM10Value(closestSite);
  }

  /**
   * Get PM2.5 value for a specific burn coup (from closest air quality station)
   */
  getPM25ValueForBurnCoup(burnCoupId: number): number | null {
    const burnCoup = this.selectedBurn?.burnCoups?.find(c => c.id === burnCoupId);
    if (!burnCoup) {
      return null;
    }

    const closestSite = this.getClosestAirQualitySiteForBurnCoup(burnCoup);
    if (!closestSite) {
      return null;
    }

    return this.getPM25Value(closestSite);
  }

  /**
   * Get color class for PM10 value for a burn coup
   */
  getPM10ColorForBurnCoup(burnCoupId: number): string {
    const value = this.getPM10ValueForBurnCoup(burnCoupId);
    return this.getPM10Color(value);
  }

  /**
   * Get color class for PM2.5 value for a burn coup
   */
  getPM25ColorForBurnCoup(burnCoupId: number): string {
    const value = this.getPM25ValueForBurnCoup(burnCoupId);
    return this.getPM25Color(value);
  }

  /**
   * Get the distance to the closest air quality station for a burn coup
   */
  getClosestAirQualityStationDistanceForBurnCoup(burnCoupId: number): number | null {
    const burnCoup = this.selectedBurn?.burnCoups?.find(c => c.id === burnCoupId);
    if (!burnCoup) {
      return null;
    }

    const closestSite = this.getClosestAirQualitySiteForBurnCoup(burnCoup);
    return closestSite ? closestSite.distance : null;
  }

  /**
   * Get the name of the closest air quality station for a burn coup
   */
  getClosestAirQualityStationNameForBurnCoup(burnCoupId: number): string | null {
    const burnCoup = this.selectedBurn?.burnCoups?.find(c => c.id === burnCoupId);
    if (!burnCoup) {
      return null;
    }

    const closestSite = this.getClosestAirQualitySiteForBurnCoup(burnCoup);
    return closestSite ? (closestSite.siteName || closestSite.name || 'Unknown Station') : null;
  }
}
